import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
} from "react";
import { DYNAMIC_PACKAGES, useSearch } from "./SearchContext";
import { isDynamicPackagePricingNormalizedEnabled } from "../utils/dynamicPackage/dynamicPackagePricingFeatureFlag";
import {
  PASSENGER_KEYS,
  ROOM_KEYS,
} from "../Components/PassengersData/passengerKeys";
import { isNumber, round2, useAppContext } from "./AppContext";
import { getUserDetails } from "../services/authService";
import { getRoomsSetByCount } from "../utils/LDSHelpers";
import { resolveLdsPackageBasePriceWithCommissionFallback } from "../utils/packages/resolveLdsPackageBookingFinancialSSOT";
import { inferGenderFromLegacyTitle } from "../utils/passengers/ldsPassengerMapper";
import {
  buildInitialRoomsAndPaxInfo,
  buildRoomCompositionSignature,
} from "../utils/passengers/buildInitialRoomsAndPaxInfo";
import { resolvePassengerSlotsForSearchOccupancy } from "../utils/passengers/resolvePassengerSlotsForSearchOccupancy";
import {
  HR_VACATION_PACKAGE_NEW_SEARCH_EVENT,
  buildPackageOccupancySignature,
  buildPaxSlotsOccupancySignature,
  buildSelectedRoomOccupancySignature,
  isPersistedVacationPackageProfilePath,
  logPackageOccupancyTrace,
  selectedRoomMatchesSearchOccupancy,
} from "../utils/passengers/packageOccupancySignature";
import { buildLdsPriceComparePayload } from "../utils/debug/buildLdsPriceComparePayload";
import { applyLdsHandlingFeeAndCardToFareDetails } from "../pricing/vacationPackageLdsAdapter";
import { runPaymentCreditClearingOnBase } from "../pricing/paymentInstallmentsPricing";
import {
  computeSupplementPriceIls,
  computeSupplementPriceIlsWithAudit,
  normalizeVpLdsDealFareCurrency,
} from "../utils/lds/baggageSupplementPricing";
import { applyLdsFixedPerPersonToOurFareDetails } from "../utils/lds/applyLdsFixedPerPersonToOurFare";
import {
  readOrganizedTourDealOurTotalFare,
  stampOrganizedTourDealHrOnFareDetails,
} from "../modules/organizedTours/utils/applyOrganizedTourDealHrToRoomFare";
import {
  ensureSelectedFlightOptionSSOT,
  extractFeFlightSelectionAuditFields,
  logFeSelectedFlightSelectionTrace,
  logVacationFlightSelectionTrace,
  buildSelectedFlightOptionFromUser,
} from "../utils/vacationPackageFlightSelectionSSOT";
import {
  hydrateMultiRoomComboLdsDealIdsFromExtended,
  resolveExtendedDealRowsForLdsMultiRoom,
  resolvePerRoomComboLdsDealId,
} from "../utils/lds/validateLdsMultiRoomComboDealIds";
import { normalizeLdsExtendedDealRows } from "../utils/lds/normalizeLdsExtendedDealRows";
import { resolveBookableLdsDealIdFromExtendedRoom } from "../utils/lds/ldsDealTokenSsot";
import {
  isVacationPackagesFlightsHotelAgentDeal,
  resolveVacationPackageRoomMarkupIls,
  sumVacationPackageConfiguredRoomMarkupIls,
  vacationPackageHasManualRoomMarkup,
  vpRoomMarkupDevTrace,
  emitVacationPackageMarkupCurrencyAudit,
} from "../utils/vacationPackageRoomAgentMarkup.js";
import {
  accumulateVpBillableRoomsIntoOurPrice,
  getVpSlotOurTotalFare,
  logVpHandleFeeRecomputeTrace,
  logVpTotalPaymentRecomputeTrace,
  roomsAndPaxInfoHasManualAddedRooms,
} from "../utils/vpTotalPaymentRoomSum";
import { resolveVpFixedMarkupBasePwccIls } from "../utils/resolveVpFixedMarkupBasePwccIls";
import { resolveVpFixedMarkupPayableTotal } from "../utils/resolveVpFixedMarkupPayableTotal";
import {
  applyVpManualAddRoomFreezeToRoomAndSlot,
  buildVpManualAddRoomFreezeFields,
  isVpManualAddedRoom,
} from "../utils/vpManualAddRoom";
import {
  applyManualRoomMarkupFieldsToSlot,
  logVpManualRoomMarkupTrace,
  recomputeManualSlotOurFareDetails,
} from "../utils/vpManualRoomMarkup";
import { pickAvailableRoomMatchingSearchSelection } from "../utils/lds/pickAvailableRoomMatchingSearchSelection";
import {
  canonicalRoomToSelectedRoom,
  copyComposeDealRefStamp,
  isRoomRowOccupancyCompatible,
  mergeCanonicalRoomIntoSelectedResult,
  occupancySlotFromPaxInfo,
} from "../utils/lds/resolveSearchCardRoom";
import { App } from "antd";

const SelectedResultContext = createContext();

/** LDS לפעמים מחזיר params כאובייקט יחיד במקום מערך */
function normalizeLdsSupplementParams(params) {
  if (params == null) return [];
  if (Array.isArray(params)) return params;
  if (typeof params === "object" && params.name != null) return [params];
  return [];
}

/** flight יכול להיות אובייקט יחיד — חייבים מערך ל-forEach / אינדקס */
function normalizeLinkedFlightsFlightArray(linkedFlights) {
  const f = linkedFlights?.flight;
  if (f == null) return [];
  return Array.isArray(f) ? f : [f];
}

function firstLegFromFlightNode(flightNode) {
  if (!flightNode?.leg) return null;
  const L = flightNode.leg;
  return Array.isArray(L) ? L[0] : L;
}

function dealIdsLooselyEqual(a, b) {
  if (a == null || b == null) return false;
  return String(a).trim() === String(b).trim();
}

function toFiniteNumber(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function resolvePublicBasePriceToAgentFromRoom(roomLike) {
  const fare = roomLike?.ourFareDetails?.ourTotalFare;
  const supplierNet = toFiniteNumber(fare?.initialPrice);
  const hrCommission = toFiniteNumber(fare?.hrCommission);
  if (
    supplierNet != null &&
    supplierNet > 0 &&
    hrCommission != null &&
    hrCommission >= 0
  ) {
    return round2(supplierNet + hrCommission);
  }
  return null;
}

function resolvePublicSelectedRoomId(roomLike) {
  const candidate = [
    roomLike?.roomId,
    roomLike?.RoomId,
    roomLike?.roomID,
    roomLike?.[ROOM_KEYS.SEQ_NUM],
    roomLike?.["@seqNum"],
  ].find((v) => v != null && String(v).trim() !== "");
  return candidate != null ? String(candidate).trim() : null;
}

/** supplement מההזמנה השמורה — תמיכה במחרוזת JSON, מערך או אובייקט (כמו SavedPackageProfile) */
function parseSavedOrderSupplementToObject(supplement) {
  if (supplement == null) return null;
  let parsed = supplement;
  if (typeof supplement === "string") {
    try {
      parsed = JSON.parse(supplement);
    } catch {
      return null;
    }
  }
  if (!parsed || typeof parsed !== "object") return null;
  if (Array.isArray(parsed)) {
    const obj = {};
    parsed.forEach((supp, index) => {
      if (supp?.["@seqNum"] != null) obj[supp["@seqNum"]] = supp;
      else obj[index + 1] = supp;
    });
    parsed = obj;
  }
  return Object.keys(parsed).length > 0 ? parsed : null;
}

function supplementsWithCheckedFromOrderObject(suppObj) {
  const out = {};
  Object.keys(suppObj).forEach((key) => {
    const supp = suppObj[key];
    out[key] = {
      ...supp,
      checked: supp.checked === true || supp.checked === "true",
    };
  });
  return out;
}

function normalizeRoomsAndPaxInfoForState(input) {
  if (!input) return [];
  if (Array.isArray(input)) return input;
  if (typeof input === "string") {
    try {
      const parsed = JSON.parse(input);
      if (Array.isArray(parsed)) return parsed;
      if (parsed && typeof parsed === "object") return Object.values(parsed);
      return [];
    } catch {
      return [];
    }
  }
  if (typeof input === "object") return Object.values(input);
  return [];
}

const INITIAL_PASSENGER = {
  [PASSENGER_KEYS.TITLE]: "",
  [PASSENGER_KEYS.GENDER]: "",
  [PASSENGER_KEYS.FIRSTNAME]: "",
  [PASSENGER_KEYS.LASTNAME]: "",
  [PASSENGER_KEYS.CELL]: "",
  [PASSENGER_KEYS.EMAIL]: "",
  [PASSENGER_KEYS.AGE]: "01/01/1990",
};

const INITIAL_KID_PASSENGER = {
  [PASSENGER_KEYS.TITLE]: "",
  [PASSENGER_KEYS.GENDER]: "",
  [PASSENGER_KEYS.FIRSTNAME]: "",
  [PASSENGER_KEYS.LASTNAME]: "",
  [PASSENGER_KEYS.AGE]: "01/01/2020",
};

const INITIAL_INFANT_PASSENGER = {
  [PASSENGER_KEYS.TITLE]: "",
  [PASSENGER_KEYS.FIRSTNAME]: "",
  [PASSENGER_KEYS.LASTNAME]: "",
  [PASSENGER_KEYS.AGE]: "01/01/2024",
};

const INITIAL_ROOM = {
  [ROOM_KEYS.ROOM_SET]: "",
  [ROOM_KEYS.ROOM_TYPE]: "",
  [ROOM_KEYS.SEQ_NUM]: "",
};

function getFirstFare(adultFare, childFare, numberOfKids, kidsAges) {
  // Check if childFare is an array
  let childFareFR = 0;

  if (Array.isArray(childFare)) {
    for (let i = 0; i < childFare.length; i++) {
      let j = 0;
      for (j = 0; j < kidsAges.length; j++) {
        const age = kidsAges[j];
        if (age >= childFare[i].ageFrom && age <= childFare[i].ageTo) {
          childFareFR += Number(childFare[i].fare);
          break;
        }
      }
      if (j === kidsAges.length) childFareFR += Number(adultFare);
    }
  } else if (!childFare) {
    childFareFR = numberOfKids * adultFare;
  } else {
    for (let j = 0; j < kidsAges.length; j++) {
      const age = kidsAges[j];
      if (age >= childFare.ageFrom && age <= childFare.ageTo)
        childFareFR += Number(childFare.fare);
      else childFareFR += Number(adultFare);
    }
  }
  return childFareFR;
}
//////////////////////////////////////
export const SelectedResultProvider = ({ children, perfStub = false }) => {
  const shouldUsePerfStub =
    perfStub ||
    (typeof window !== "undefined" &&
      window.__PERF_SELECTED_RESULT_STUB__ === true);
  const autofillIntervalTickRef = useRef(0);
  const { notification } = App.useApp();
  const {
    currencyRate,
    getCreditCardFeePercent,
    getBaggageFeeFlat,
  } = useAppContext();

  // Handle fee data - must be defined before applyHandlingFeeToFareDetails
  const [handleFeeData, setHandleFeeData] = useState({
    type: "+",
    value: 0,
  });

  const [creditCardFee, setCreditCardFee] = useState(0);

  const calculateCreditCardFee = (priceToPay) => {
    const clearing = runPaymentCreditClearingOnBase({
      baseForFeeIls: parseFloat(priceToPay),
      feePercent: getCreditCardFeePercent(),
      roundMoney: round2,
    });
    const fee = clearing.creditCardFee ?? 0;
    setCreditCardFee(fee);
    return fee;
  };

  const {
    searchTab,
    roomInfo,
    calculateLDSDealFare,
    calculateLDSDealFareForRoomsComposition,
    linkedFlightsDealPrice: searchLinkedFlightsDealPrice,
    categories,
    currentSearchRequestId,
  } = useSearch();

  /** SearchProvider מגדיר פונקציה מקומית — זהות משתנה בכל רינדור; אסור ב-deps של useEffect (לולאת setState). */
  const calculateLDSDealFareRef = useRef(calculateLDSDealFare);
  calculateLDSDealFareRef.current = calculateLDSDealFare;
  const calculateLDSDealFareForRoomsCompositionRef = useRef(
    calculateLDSDealFareForRoomsComposition,
  );
  calculateLDSDealFareForRoomsCompositionRef.current =
    calculateLDSDealFareForRoomsComposition;

  /** @type {import("react").MutableRefObject<((roomsOverride?: object[], traceOpts?: object) => object|undefined)|null>} */
  const calculateLdsTotalPaymentRef = useRef(null);

  const roomsPriceTableEffectRunRef = useRef(0);

  const { rooms: roomsComposition } = roomInfo;
  const roomsCompositionRef = useRef(roomsComposition);
  roomsCompositionRef.current = roomsComposition;
  const currentSearchRequestIdRef = useRef(currentSearchRequestId);
  currentSearchRequestIdRef.current = currentSearchRequestId;
  const roomsCompositionSignature = useMemo(
    () => buildRoomCompositionSignature({ rooms: roomsComposition }),
    [roomsComposition],
  );
  const currentOccupancySignature = useMemo(
    () => buildPackageOccupancySignature({ rooms: roomsComposition }),
    [roomsComposition],
  );
  const [extendedDealApiData, setExtendedDealApiData] = useState([]);
  const [roomsPriceTable, setRoomsPriceTable] = useState([]);
  const [hotelOrder, setHotelOrder] = useState(null);
  const [dynamicOrder, setDynamicOrder] = useState(null);

  const [hotelDetails, setHotelDetails] = useState(null);
  const [hotelResource, setHotelResource] = useState(null);
  /** LDS /getHotelInfo server-side normalized block (see services/lds_hotel_info_normalize.py). */
  const [ldsHotelInfoNormalized, setLdsHotelInfoNormalized] = useState(null);
  const [ourSavedLDSOrder, setOurSavedLDSOrder] = useState(null);

  const [isConditionsConfirmed, setIsConditionsConfirmed] = useState(
    false || !!ourSavedLDSOrder
  );
  const [selectedResult, _setSelectedResult] = useState();
  const selectedResultRef = useRef(null);
  selectedResultRef.current = selectedResult;
  const [packageAvailable, setPackageAvailable] = useState(true);
  /** Packages/profile: block spinner when extended matrix fails / empty; show message in sidebar/footer */
  const [packageProfilePriceBlockMessage, setPackageProfilePriceBlockMessage] =
    useState(null);
  
  // Wrapper function for setSelectedResult that clears supplements when package changes
  const setSelectedResult = useCallback((newSelectedResult) => {
    const beforeAudit = extractFeFlightSelectionAuditFields(selectedResultRef.current);
    const normalizedNextSelectedResult = ensureSelectedFlightOptionSSOT(newSelectedResult, {
      sourceIfMissing: "default_first",
    });
    const afterAudit = extractFeFlightSelectionAuditFields(normalizedNextSelectedResult);
    if (
      beforeAudit.selectedFlightIndex !== afterAudit.selectedFlightIndex ||
      beforeAudit.optionSignature !== afterAudit.optionSignature ||
      beforeAudit.source !== afterAudit.source ||
      beforeAudit.isDefault !== afterAudit.isDefault
    ) {
      logFeSelectedFlightSelectionTrace(beforeAudit, afterAudit, {
        context: "SelectedResultContext.setSelectedResult",
        default_first_applied_by_normalize:
          afterAudit.source === "default_first" && beforeAudit.source !== "default_first",
      });
    }
    const currentDealId =
      selectedResultRef.current?.dealId ??
      selectedResultRef.current?.fullDeal?.dealId ??
      null;
    const newDealId =
      normalizedNextSelectedResult?.dealId ??
      normalizedNextSelectedResult?.fullDeal?.dealId ??
      null;
    
    // If package changed (dealId changed), clear supplements IMMEDIATELY
    if (currentDealId && newDealId && currentDealId !== newDealId) {
      console.log('[SelectedResultContext] Package changed - clearing all supplements', {
        oldDealId: currentDealId,
        newDealId: newDealId
      });
      
      // Clear supplements IMMEDIATELY using direct state setter to bypass validation
      _setLdsSupplement({});
      // Stale ourPrice would win in computeVacationPackageCheckoutPricingNis (context first) and show wrong sidebar totals
      setOurPrice({});
      setOurSavedLDSOrder(null);
      supplementsInitializedRef.current = false;
      lastDealIdRef.current = null;
      prevSelectedResultRef.current = null;
      currentDealIdRef.current = null; // Reset dealId tracking
      setSelectedRoom(null);
      setSelectedPublicRoomPriceSnapshot(null);
    }
    
    // Update dealId tracking
    if (newDealId) {
      currentDealIdRef.current = newDealId;
    }
    
    // Set the new selectedResult
    _setSelectedResult(normalizedNextSelectedResult);
  }, []);
  
  // Trip Guaranty insurance state
  const [tripGuarantySelected, setTripGuarantySelected] = useState(false);
  const [tripGuarantyQuote, setTripGuarantyQuote] = useState(null);

  useEffect(() => {
    if (searchTab !== DYNAMIC_PACKAGES) setDynamicOrder(null);
  }, [searchTab]);

  const [ldsPassengerFieldErrors, setLdsPassengerFieldErrors] = useState({});
  const clearLdsPassengerFieldErrors = useCallback(
    () => setLdsPassengerFieldErrors({}),
    []
  );

  const [currentStageIndex, setCurrentStageIndex] = useState(0);
  const [roomsAndPaxInfo, _setRoomsAndPaxInfo] = React.useState(
    normalizeRoomsAndPaxInfoForState(getInitialPassengersInfo())
  );
  const setRoomsAndPaxInfoEventsRef = useRef(0);

  // Debounce timer for heavy operations
  const heavyOpsTimerRef = React.useRef(null);

  /** Reassigned each render so the stable setRoomsAndPaxInfo callback always runs latest logic (ref indirection). */
  const setRoomsAndPaxInfoImplRef = useRef(null);
  setRoomsAndPaxInfoImplRef.current = (newRoomsAndPaxInfo, init = true, options = {}) => {
    const immediateValidation = options?.immediateValidation === true;
    const validationOptions = {
      requireContactForAllAdults: options?.requireContactForAllAdults,
    };
    const coercedInput = newRoomsAndPaxInfo == null ? [] : newRoomsAndPaxInfo;
    const normalizedRoomsAndPaxInfo = normalizeRoomsAndPaxInfoForState(coercedInput);
    if (!Array.isArray(newRoomsAndPaxInfo) && newRoomsAndPaxInfo != null) {
      console.warn("[CUSTOMER_OFFER_CRASH_GUARD]", newRoomsAndPaxInfo);
    }
    setRoomsAndPaxInfoEventsRef.current += 1;
    // Update state immediately for UI responsiveness
    _setRoomsAndPaxInfo(normalizedRoomsAndPaxInfo);
    setLdsPassengerFieldErrors({});
    // Defer heavy operations to avoid blocking the UI during typing
    if (init && immediateValidation) {
      setFieldsFilled(areAllFieldsFilled(normalizedRoomsAndPaxInfo, validationOptions));
      setRoomsSelected(areAllRoomsSelected(normalizedRoomsAndPaxInfo));
      updateNameInSupplement(normalizedRoomsAndPaxInfo);
      return;
    }
    if (init) {
      // Clear previous timer
      if (heavyOpsTimerRef.current) {
        clearTimeout(heavyOpsTimerRef.current);
      }
      // Debounce heavy operations - only run after user stops typing for 500ms
      heavyOpsTimerRef.current = setTimeout(() => {
        setFieldsFilled(areAllFieldsFilled(normalizedRoomsAndPaxInfo, validationOptions));
        setRoomsSelected(areAllRoomsSelected(normalizedRoomsAndPaxInfo));
        updateNameInSupplement(normalizedRoomsAndPaxInfo);
        heavyOpsTimerRef.current = null;
      }, 500);
    }
  };

  const setRoomsAndPaxInfo = useCallback(
    (newRoomsAndPaxInfo, init = true, options = {}) =>
      setRoomsAndPaxInfoImplRef.current?.(newRoomsAndPaxInfo, init, options),
    []
  );

  const vacationPackageRoomsMarkupSig = useMemo(() => {
    if (!selectedResult || !isVacationPackagesFlightsHotelAgentDeal(selectedResult)) {
      return "";
    }
    if (handleFeeData?.type !== "+") return "";
    const gv = Number(handleFeeData.value) || 0;
    const slotParts = (roomsAndPaxInfo || []).map(
      (r) => `${String(r.agentMarkup ?? "")}:${r.agentMarkupManuallyEdited ? 1 : 0}`
    );
    return `${gv}|${slotParts.join("|")}`;
  }, [
    roomsAndPaxInfo,
    selectedResult,
    handleFeeData?.type,
    handleFeeData?.value,
  ]);

  const applyHandlingFeeToFareDetails = useCallback(
    (ourFareDetails, numberOfRooms, roomSlotForMarkup = null) => {
      const vpFixed =
        isVacationPackagesFlightsHotelAgentDeal(selectedResult) &&
        handleFeeData?.type === "+";
      const perRoomMarkup =
        vpFixed && roomSlotForMarkup != null
          ? resolveVacationPackageRoomMarkupIls(roomSlotForMarkup, handleFeeData)
          : null;
      const extraOpts =
        vpFixed &&
        roomSlotForMarkup != null &&
        perRoomMarkup != null &&
        Number.isFinite(Number(perRoomMarkup)) &&
        Number(perRoomMarkup) >= 0
          ? { perRoomFixedMarkupIls: Number(perRoomMarkup) }
          : {};
      const { ourFareDetails: out } = applyLdsHandlingFeeAndCardToFareDetails({
        ourFareDetails,
        numberOfRooms,
        handleFeeData,
        getCreditCardFeePercent,
        ...extraOpts,
      });

      try {
        if (
          vpFixed &&
          roomSlotForMarkup != null &&
          extraOpts.perRoomFixedMarkupIls !== undefined &&
          typeof import.meta !== "undefined" &&
          import.meta.env?.DEV
        ) {
          const inTf = ourFareDetails?.ourTotalFare;
          const outTf = out?.ourTotalFare;
          emitVacationPackageMarkupCurrencyAudit({
            baseFareCurrency:
              inTf?.originalCurrency ?? inTf?.fx_currency ?? null,
            baseFareOriginal: inTf?.initialPrice ?? null,
            baseFareIls:
              Number.isFinite(Number(inTf?.priceWithCommission))
                ? Number(inTf.priceWithCommission)
                : null,
            roomMarkupIls: Number(perRoomMarkup),
            finalRoomPriceIls: Number.isFinite(
              Number(outTf?.priceWithCommissionAndCreditCardFee)
            )
              ? Number(outTf.priceWithCommissionAndCreditCardFee)
              : Number.isFinite(Number(outTf?.priceWithHandleFee))
                ? Number(outTf.priceWithHandleFee)
                : null,
          });
        }
      } catch {
        /* ignore */
      }

      return out;
    },
    [handleFeeData, getCreditCardFeePercent, selectedResult]
  );

  useEffect(() => {
    if (!Array.isArray(roomsAndPaxInfo)) {
      console.warn("[CUSTOMER_OFFER_CRASH_GUARD]", roomsAndPaxInfo);
    }
    console.log("[PROFILE_ROOMS_AND_PAX_AFTER_NORMALIZE]", {
      source: "SelectedResultContext",
      roomsCount: Array.isArray(roomsAndPaxInfo) ? roomsAndPaxInfo.length : 0,
      rooms: Array.isArray(roomsAndPaxInfo)
        ? roomsAndPaxInfo.map((room, idx) => ({
            idx,
            roomId:
              room?.roomId ??
              room?.room?.roomId ??
              room?.room?.["@seqNum"] ??
              null,
            roomName: room?.roomName ?? room?.room?.roomType ?? null,
            adults: Array.isArray(room?.adults) ? room.adults.length : 0,
            kids: Array.isArray(room?.kids) ? room.kids.length : 0,
            infants: Array.isArray(room?.infants) ? room.infants.length : 0,
          }))
        : [],
    });
  }, [roomsAndPaxInfo]);

  // init roomsPriceTable
  // we cannot use the lds total fare because it doesn't include infant and show only the sum of the first room
  // therefore we need to calc the total by a sum of each pax type (multiply by the number of pax)
  // this result in a roomsPriceTable where each row is a room composition and each col is a specific room (from extended deal api)
  useEffect(() => {
    const dealTypeMatrix =
      selectedResult?.dealType ?? selectedResult?.dealtype ?? "";
    const isOrganizedTourDeal = dealTypeMatrix === "OrganizedTour";

    if (isDynamicPackagePricingNormalizedEnabled() && searchTab === DYNAMIC_PACKAGES) {
      setRoomsPriceTable([]);
      return;
    }

    if (!selectedResult) return;
    if (Array.isArray(extendedDealApiData) && extendedDealApiData.length === 0) {
      const embeddedRaw =
        selectedResult.extendedDeal ??
        selectedResult.extended ??
        selectedResult.fullDeal?.extendedDeal ??
        selectedResult.fullDeal?.extended ??
        null;
      if (embeddedRaw != null) {
        const embeddedRows = normalizeLdsExtendedDealRows(embeddedRaw);
        if (embeddedRows.length > 0) {
          setExtendedDealApiData(embeddedRows);
        }
      }
      return;
    }
    
    if (!roomsAndPaxInfo || roomsAndPaxInfo.length === 0) {
      const defaultRoomsAndPaxInfo = normalizeRoomsAndPaxInfoForState(
        buildInitialRoomsAndPaxInfo({ rooms: roomsComposition }),
      );
      if (defaultRoomsAndPaxInfo.length === 0) return;
      _setRoomsAndPaxInfo(defaultRoomsAndPaxInfo);
      return;
    }
    if (!extendedDealApiData) {
      setPackageAvailable(false);
      notification.error({
        message: "חבילה אינה זמינה",
        description: "אנא פנה לצוות התמיכה",
        duration: 5,
        placement: "bottomRight",
      });
      if (selectedResult.extended) {
        setExtendedDealApiData(normalizeLdsExtendedDealRows(selectedResult.extended));
      }
      return;
    }
    const { fareDetails, dealType, hotel, tour, supplierDetails } =
      selectedResult;

    console.log("SelectedResultContext: Creating roomsPriceTable with:", {
      roomsAndPaxInfo: roomsAndPaxInfo,
      extendedDealApiData: extendedDealApiData
    });
    console.log("SelectedResultContext: Number of rooms available for display:", extendedDealApiData.length);
    const { rows: normalizedExtendedRows } = resolveExtendedDealRowsForLdsMultiRoom({
      extendedDealApiData,
      selectedResult,
    });
    const roomsPriceTable = roomsAndPaxInfo.map((roomPaxInfo) =>
      normalizedExtendedRows.map((roomSuggestion) => {
        let {
          childFare = { fare: 0 },
          adultFare,
          infantFare = 0,
          totalFare = 0,
          ldsDealId,
        } = roomSuggestion;

        const occupancySlot = occupancySlotFromPaxInfo(roomPaxInfo);
        if (!isRoomRowOccupancyCompatible(roomSuggestion, occupancySlot)) {
          return null;
        }

        if (roomPaxInfo?.infants.length && !infantFare) {
          return null;
        }

        if (!childFare.length || !childFare.fare) {
          childFare.fare = adultFare;
        }

        const kidsAges = roomPaxInfo.kids.map(
          (kid) => kid[PASSENGER_KEYS.KIS_SEARCH_AGE]
        );

        if (!totalFare) {
          totalFare = round2(
            Number(adultFare) * roomPaxInfo.adults.length +
              Number(
                getFirstFare(
                  adultFare,
                  childFare,
                  roomPaxInfo.kids.length,
                  kidsAges
                )
              ) +
              Number(infantFare) * roomPaxInfo.infants.length
          );
        } else if (infantFare) {
          totalFare =
            Number(totalFare) + Number(infantFare) * roomPaxInfo.infants.length;
        }

        const ourFareDetails = calculateLDSDealFareRef.current({
          dealFare: {
            adultFare,
            totalFare,
            childFareExt: childFare,
            infantFare,
          },
          fareDetails,
          dealType,
          hotel,
          tour,
          supplierDetails,
        });
        const calculatedRoomPrice = Number(
          ourFareDetails?.ourTotalFare?.priceWithCommissionAndCreditCardFee
        );
        if (!(Number.isFinite(calculatedRoomPrice) && calculatedRoomPrice > 0)) {
          return null;
        }

        // FIXED_PER_PERSON is only embedded in ForRoomsComposition — bare
        // calculateLDSDealFare leaves LDS gross + % HR (e.g. 5990×1.04=$6230).
        // Profile soft-fill uses this matrix, so apply the same per-person embed here.
        applyLdsFixedPerPersonToOurFareDetails(ourFareDetails, {
          adults: Array.isArray(roomPaxInfo?.adults)
            ? roomPaxInfo.adults.length
            : Number(roomPaxInfo?.adults) || 0,
          kids: Array.isArray(roomPaxInfo?.kids)
            ? roomPaxInfo.kids.length
            : Number(roomPaxInfo?.kids) || 0,
          totalFare,
          currency: fareDetails?.currency,
          currencyRate,
          creditCardFeePercent: getCreditCardFeePercent(),
        });

        // Apply handling fee (עמלה) that agent added to the fare details
        const numberOfRooms = roomsAndPaxInfo.length || 1;
        const ourFareDetailsWithHandleFee = applyHandlingFeeToFareDetails(
          ourFareDetails,
          numberOfRooms,
          roomPaxInfo
        );
        if (isOrganizedTourDeal) {
          stampOrganizedTourDealHrOnFareDetails(
            ourFareDetailsWithHandleFee,
            readOrganizedTourDealOurTotalFare(selectedResult)
          );
        }

        const bookableLdsDealId =
          resolveBookableLdsDealIdFromExtendedRoom(roomSuggestion) ||
          ldsDealId ||
          "";
        const suggestionWithBookable = bookableLdsDealId
          ? { ...roomSuggestion, ldsDealId: bookableLdsDealId }
          : roomSuggestion;
        return {
          roomSuggestion: suggestionWithBookable,
          ourFareDetails: ourFareDetailsWithHandleFee,
          ldsDealId: bookableLdsDealId,
        };
      })
    );

    const patchedRoomsPriceTable = JSON.parse(JSON.stringify(roomsPriceTable));

    // Update all rooms with new prices (including handling fee recalculation)
    let tempPassengerInfo = JSON.parse(JSON.stringify(roomsAndPaxInfo));
    let hasChanges = false;
    const currentNumberOfRooms = roomsAndPaxInfo.length || 1;
    const comboOptsForRoomPick = buildMultiRoomComboOptions(
      selectedResult,
      normalizedExtendedRows
    );

    tempPassengerInfo.forEach((roomPaxInfo, roomAndPaxInfoIndex) => {
      if (
        isVpManualAddedRoom(roomPaxInfo?.room, roomPaxInfo) &&
        getVpSlotOurTotalFare(roomPaxInfo)
      ) {
        const recomputeResult = recomputeManualSlotOurFareDetails(roomPaxInfo, {
          handleFeeData,
          numberOfRooms: currentNumberOfRooms,
          getCreditCardFeePercent,
        });
        if (recomputeResult?.updated) {
          applyManualRoomMarkupFieldsToSlot(
            tempPassengerInfo[roomAndPaxInfoIndex],
            recomputeResult
          );
          hasChanges = true;

          const selectedSeqNum = roomPaxInfo.room?.[ROOM_KEYS.SEQ_NUM];
          const slotRow = patchedRoomsPriceTable[roomAndPaxInfoIndex];
          if (Array.isArray(slotRow) && selectedSeqNum != null) {
            const matchIdx = slotRow.findIndex(
              (row) => row?.roomSuggestion?.["@seqNum"] === selectedSeqNum
            );
            if (matchIdx >= 0) {
              slotRow[matchIdx] = {
                ...slotRow[matchIdx],
                ourFareDetails: recomputeResult.updated,
              };
            }
          }

          logVpManualRoomMarkupTrace({
            roomIndex: roomAndPaxInfoIndex,
            addedFromRoomOptions: true,
            baseManualRoomPriceIls: recomputeResult.baseManualRoomPriceIls,
            roomLevelHandleFeeIls: recomputeResult.roomLevelHandleFeeIls,
            priceBeforeMarkup: recomputeResult.priceBeforeMarkup,
            priceAfterMarkup: recomputeResult.priceAfterMarkup,
            includedInSidebarTotal: true,
            source: "manual_add_room_with_room_markup",
          });
        }
        return;
      }

      const currentRoomPrices = roomsPriceTable[roomAndPaxInfoIndex];
      
      if (!currentRoomPrices || currentRoomPrices.length === 0) return;
      
      // Filter out null/undefined rooms and sort by price (cheapest first)
      // Use price with handling fee and credit card fee for accurate sorting
      const availableRooms = currentRoomPrices
        ?.filter((roomPriceData) => roomPriceData !== null && roomPriceData !== undefined)
        ?.sort((a, b) => {
          const priceA = Number(
            a.ourFareDetails?.ourTotalFare?.priceWithCommissionAndCreditCardFee ||
            a.ourFareDetails?.ourTotalFare?.priceWithHandleFee ||
            a.ourFareDetails?.ourTotalFare?.priceWithCommission || 0
          );
          const priceB = Number(
            b.ourFareDetails?.ourTotalFare?.priceWithCommissionAndCreditCardFee ||
            b.ourFareDetails?.ourTotalFare?.priceWithHandleFee ||
            b.ourFareDetails?.ourTotalFare?.priceWithCommission || 0
          );
          return priceA - priceB;
        });

      if (!availableRooms || availableRooms.length === 0) return;

      // If room is already selected, keep that room (do not auto-switch to cheapest)
      if (roomPaxInfo.room?.[ROOM_KEYS.SEQ_NUM]) {
        const selectedSeqNum = roomPaxInfo.room[ROOM_KEYS.SEQ_NUM];
        const matchingRoom = availableRooms.find(
          (roomPriceData) => roomPriceData.roomSuggestion?.["@seqNum"] === selectedSeqNum
        );
        
        if (matchingRoom) {
          // Update the selected room with new price from roomsPriceTable
          // The price already includes handling fee calculated with current number of rooms
          const { roomSuggestion, ourFareDetails, ldsDealId } = matchingRoom;
          setRoomInfo_helper(
            { ...roomSuggestion, ourFareDetails, ldsDealId },
            tempPassengerInfo,
            roomAndPaxInfoIndex,
            comboOptsForRoomPick
          );
          hasChanges = true;
        } else {
          // seqNum lost/stale: rematch by the agent's current selection only — never cheapest,
          // and never re-apply a different search-card room type over what was chosen.
          // If rematch fails, keep the existing selection as-is (checkout/payment must not swap).
          const preferred = pickAvailableRoomMatchingSearchSelection(
            availableRooms,
            { selectedRoom: roomPaxInfo.room },
            roomPaxInfo.room,
            roomAndPaxInfoIndex,
            occupancySlotFromPaxInfo(roomPaxInfo)
          );
          if (!preferred) {
            return;
          }
          const { roomSuggestion, ourFareDetails, ldsDealId } = preferred;
          setRoomInfo_helper(
            { ...roomSuggestion, ourFareDetails, ldsDealId },
            tempPassengerInfo,
            roomAndPaxInfoIndex,
            comboOptsForRoomPick
          );
          hasChanges = true;
        }
      } else if (isOrganizedTourDeal) {
        // Do not soft-fill the cheapest extended room. That reprice replaces the
        // search-card package (e.g. ₪10,358 → ₪10,024) before the agent picks a room.
        return;
      } else {
        // Vacation packages: leave empty for the agent to choose.
        // Never auto-pick (including search-card soft fill) and never toast.
        return;
      }
    });

    setRoomsPriceTable(patchedRoomsPriceTable);

    let roomsToPersist = tempPassengerInfo;
    let comboHydrationChanged = false;
    if (roomsToPersist.length >= 2) {
      const comboOpts = buildMultiRoomComboOptions(
        selectedResult,
        normalizedExtendedRows
      );
      const hydrated = hydrateMultiRoomComboLdsDealIdsFromExtended(
        roomsToPersist,
        comboOpts
      );
      comboHydrationChanged = hydrated.some((slot, i) => {
        const nextTok = slot?.room?.ldsDealId ?? slot?.room?.[ROOM_KEYS.LDS_DEAL_ID];
        const prevTok =
          tempPassengerInfo[i]?.room?.ldsDealId ??
          tempPassengerInfo[i]?.room?.[ROOM_KEYS.LDS_DEAL_ID];
        return nextTok !== prevTok;
      });
      if (comboHydrationChanged) {
        roomsToPersist = hydrated;
      }
    }

    if (hasChanges || comboHydrationChanged) {
      setRoomsAndPaxInfo(roomsToPersist, false);
    }

    if (
      (isVacationPackagesFlightsHotelAgentDeal(selectedResult) || isOrganizedTourDeal) &&
      !publicSavedOfferPriceLockRef.current &&
      tempPassengerInfo.length > 0 &&
      tempPassengerInfo.some((s) => getVpSlotOurTotalFare(s))
    ) {
      calculateLdsTotalPaymentRef.current?.(tempPassengerInfo, {
        source: isOrganizedTourDeal
          ? "org_tour_rooms_matrix_sync"
          : "rooms_matrix_markup_sync",
        recomputeTriggered: true,
        dependencyChangedKeys: ["vacationPackageRoomsMarkupSig", "handleFeeData"],
      });
    }
  }, [
    searchTab,
    extendedDealApiData,
    roomsAndPaxInfo.length,
    roomsCompositionSignature,
    vacationPackageRoomsMarkupSig,
    selectedResult,
    handleFeeData,
    applyHandlingFeeToFareDetails,
    currencyRate,
    getCreditCardFeePercent,
    // notification from App.useApp() may change identity each render — do not list it here
    // calculateLDSDealFare — דרך ref בלבד (ראו calculateLDSDealFareRef)
  ]);

  const [selectedRoom, setSelectedRoom] = useState(null);
  const [selectedPublicRoomPriceSnapshot, setSelectedPublicRoomPriceSnapshot] =
    useState(null);
  const [agentData, setAgentData] = useState(getUserDetails());

  const updateHandleFeeData = (type, value) => {
    setHandleFeeData({ type, value });
  };

  useEffect(() => {
    if (!selectedResult || !isVacationPackagesFlightsHotelAgentDeal(selectedResult)) {
      return;
    }
    if (handleFeeData?.type !== "+") return;
    vpRoomMarkupDevTrace("[VACATION_PACKAGE_GLOBAL_MARKUP_SYNC_TO_ROOMS]", {
      dealId:
        selectedResult?.dealId ?? selectedResult?.fullDeal?.dealId ?? selectedResult?.id ?? null,
      globalMarkupIls: handleFeeData?.value ?? 0,
      roomSlots: Array.isArray(roomsAndPaxInfo)
        ? roomsAndPaxInfo.map((r, i) => ({
            i,
            manuallyEdited: Boolean(r.agentMarkupManuallyEdited),
            agentMarkup: r.agentMarkup ?? null,
          }))
        : [],
    });
  }, [selectedResult?.dealId, handleFeeData?.type, handleFeeData?.value, roomsAndPaxInfo, selectedResult]);

  const [ourPrice, setOurPrice] = useState({});

  const [canonicalBackendProfilePricing, setCanonicalBackendProfilePricing] =
    useState(null);

  /** Payable profile UI SSOT: live booking total only (supplements, handle fee, CC). */
  const packageProfileDisplayOurPrice = useMemo(() => ourPrice, [ourPrice]);

  /** נעילת מחיר להצעה ציבורית (packages/profile/:id) — מונע calculateLdsTotalPayment שדורס ourprice מהשרת */
  const publicSavedOfferPriceLockRef = useRef(false);
  const [isPublicSavedOfferPriceLocked, setIsPublicSavedOfferPriceLocked] =
    React.useState(false);
  const setPublicSavedOfferPriceLock = useCallback((locked) => {
    const v = !!locked;
    publicSavedOfferPriceLockRef.current = v;
    setIsPublicSavedOfferPriceLocked(v);
  }, []);

  const [fieldsFilled, setFieldsFilled] = React.useState(false);
  const [roomsSelected, setRoomsSelected] = React.useState(false);
  // ============================================
  // LUGGAGE SUPPLEMENTS MANAGEMENT - CLEAN REWRITE
  // ============================================
  // Core principle: Supplements are tied to a specific dealId
  // When dealId changes, supplements MUST be cleared immediately
  // ============================================
  
  const [ldsSupplement, _setLdsSupplement] = React.useState(
    getInitialLdsSupplements()
  );
  
  // Track current dealId to detect package changes
  const currentDealIdRef = useRef(null);
  
  // Wrapper for setLdsSupplement that validates dealId
  const setLdsSupplement = useCallback((newSupplements) => {
    const currentDealId = selectedResult?.dealId;
    
    // If we have a current dealId and it doesn't match, don't set supplements
    if (currentDealId && currentDealIdRef.current && currentDealId !== currentDealIdRef.current) {
      console.warn('[SelectedResultContext] setLdsSupplement - DealId mismatch, ignoring supplements', {
        currentDealId,
        trackedDealId: currentDealIdRef.current
      });
      return;
    }
    
    // Update tracked dealId if we have a selectedResult
    if (currentDealId) {
      currentDealIdRef.current = currentDealId;
    }
    
    _setLdsSupplement(newSupplements);
    try {
      const rows =
        newSupplements && typeof newSupplements === "object"
          ? Object.values(newSupplements)
          : [];
      const selectedRows = rows.filter((row) => {
        if (!row || typeof row !== "object") return false;
        if (row.checked === true || row.selected === true || row.isSelected === true) return true;
        const qty = Number(
          row.selectedQuantity ?? row.quantity ?? row.qty ?? row.count ?? row.selectedCount ?? 0
        );
        return Number.isFinite(qty) && qty > 0;
      });
      if (import.meta.env.DEV) {
        console.warn("[BAGGAGE_SELECTED_STATE_AFTER_CLICK]", {
          dealId: currentDealId || null,
          totalRows: rows.length,
          selectedRows: selectedRows.length,
          selectedPreview: selectedRows.slice(0, 8).map((row) => ({
            id: row?.id ?? null,
            type: row?.type ?? null,
            checked: row?.checked ?? null,
            selected: row?.selected ?? row?.isSelected ?? null,
            quantity:
              row?.selectedQuantity ??
              row?.quantity ??
              row?.qty ??
              row?.count ??
              row?.selectedCount ??
              null,
            fltRef: row?.fltRef ?? row?.flight?.fltRef ?? null,
            ourFare: row?.ourFare ?? row?.fare ?? null,
          })),
        });
      }
    } catch (err) {
      if (import.meta.env.DEV) {
        console.warn("[BAGGAGE_SELECTED_STATE_AFTER_CLICK]", {
          dealId: currentDealId || null,
          error: err?.message || String(err),
        });
      }
    }
  }, [selectedResult?.dealId]);

  const linkedFlightsDealPriceHandleFee = useMemo(() => {
    if (!searchLinkedFlightsDealPrice) return 0;
    if (handleFeeData.type === "%") {
      return (searchLinkedFlightsDealPrice * handleFeeData.value) / 100;
    } else if (handleFeeData.type === "+") {
      return handleFeeData.value;
    } else {
      return 0;
    }
  }, [searchLinkedFlightsDealPrice, handleFeeData]);

  const linkedFlightsDealPrice = useMemo(() => {
    if (!searchLinkedFlightsDealPrice) return 0;
    return searchLinkedFlightsDealPrice + linkedFlightsDealPriceHandleFee;
  }, [searchLinkedFlightsDealPrice, linkedFlightsDealPriceHandleFee]);

  const paxOccupancySignature = useMemo(
    () => buildPaxSlotsOccupancySignature(roomsAndPaxInfo),
    [roomsAndPaxInfo],
  );

  //////////////////////////////////////
  React.useEffect(() => {
    const decision = resolvePassengerSlotsForSearchOccupancy({
      roomsComposition,
      roomsAndPaxInfo,
      ourSavedLDSOrder,
      selectedResultDealType:
        selectedResult?.dealType || selectedResult?.dealtype || "",
    });
    if (decision.action === "wait" || decision.action === "keep") {
      logPackageOccupancyTrace("roomsComposition_effect", {
        searchId: currentSearchRequestId,
        selectedResultId: selectedResult?.dealId ?? selectedResult?.id ?? null,
        ldsDealId:
          selectedResult?.ldsDealId ??
          selectedResult?.linkedFlights?.[0]?.ldsDealId ??
          null,
        roomsComposition,
        roomsAndPaxInfo,
        selectedRoom: selectedResult?.selectedRoom ?? selectedRoom,
        currentOccupancySignature,
        selectedRoomOccupancySignature: buildSelectedRoomOccupancySignature(
          selectedResult?.selectedRoom ?? selectedRoom,
        ),
      });
      return;
    }
    const nextSlots = Array.isArray(decision.roomsAndPaxInfo)
      ? decision.roomsAndPaxInfo
      : [];
    if (nextSlots.length === 0) return;
    setRoomsAndPaxInfo(nextSlots);
    const roomToCheck = selectedResult?.selectedRoom ?? selectedRoom;
    if (
      !selectedRoomMatchesSearchOccupancy(
        roomToCheck,
        roomsComposition,
        getRoomsSetByCount,
      )
    ) {
      setSelectedRoom(null);
      setSelectedPublicRoomPriceSnapshot(null);
      if (selectedResult?.selectedRoom) {
        const nextSelected = {
          ...selectedResult,
          selectedRoom: null,
        };
        copyComposeDealRefStamp(selectedResult, nextSelected);
        _setSelectedResult(nextSelected);
      }
    }
    logPackageOccupancyTrace("roomsComposition_effect", {
      searchId: currentSearchRequestId,
      selectedResultId: selectedResult?.dealId ?? selectedResult?.id ?? null,
      ldsDealId:
        selectedResult?.ldsDealId ??
        selectedResult?.linkedFlights?.[0]?.ldsDealId ??
        null,
      roomsComposition,
      roomsAndPaxInfo: nextSlots,
      selectedRoom: null,
      currentOccupancySignature,
      selectedRoomOccupancySignature: null,
    });
  }, [
    roomsCompositionSignature,
    paxOccupancySignature,
    ourSavedLDSOrder?.id,
    selectedResult?.dealType,
    selectedResult?.dealtype,
    ourSavedLDSOrder?.deal_type,
    ourSavedLDSOrder?.dealType,
    currentOccupancySignature,
    currentSearchRequestId,
  ]);

  const moveNextStage = () => setCurrentStageIndex((i) => i + 1);

  ////////////////////////////////////////////////
  function getInitialLdsSupplements() {
    return {};
  }
  ////////////////////////////////////////////////
  function getInitialPassengersInfo() {
    return buildInitialRoomsAndPaxInfo({
      rooms: roomsCompositionRef.current || roomsComposition,
    });
  }

  // Add comission to a price
  const addHandlingComission = (price) => {
    let result = price;
    try {
      if (handleFeeData.type === "%") {
        result = round2(price + (price * handleFeeData.value) / 100);
      } else if (handleFeeData.type === "+") {
        const numberOfRooms = roomsAndPaxInfo.length;
        result = price + round2(handleFeeData.value / numberOfRooms);
      } else {
        result = price;
      }
    } catch (e) {
      result = price;
    }
    result = result + calculateCreditCardFee(result);
    return round2(result);
  };

  ////////////////////////////////////////////////
  function areAllFieldsFilled(dataToCheck = null, options = {}) {
    const data = dataToCheck || roomsAndPaxInfo;
    const requireContactForAllAdults = options?.requireContactForAllAdults !== false;
    // Field definitions for adults and kids
    const adultFieldsBase = [
      PASSENGER_KEYS.LASTNAME,
      PASSENGER_KEYS.FIRSTNAME,
      PASSENGER_KEYS.AGE,
      PASSENGER_KEYS.GENDER,
    ];
    const adultContactFields = [PASSENGER_KEYS.CELL, PASSENGER_KEYS.EMAIL];
    const kidFields = [
      PASSENGER_KEYS.AGE,
      PASSENGER_KEYS.LASTNAME,
      PASSENGER_KEYS.FIRSTNAME,
      PASSENGER_KEYS.GENDER,
    ];

    ////////////////////////////////////////////////
    // Helper function to check if all required fields are filled in an object
    ////////////////////////////////////////////////
    function isComplete(person, requiredFields) {
      return requiredFields.every((field) => {
        if (field === PASSENGER_KEYS.GENDER) {
          const g = person[field];
          if (g !== undefined && g !== "" && g !== null) return true;
          return !!inferGenderFromLegacyTitle(person[PASSENGER_KEYS.TITLE]);
        }
        return (
          person[field] !== undefined &&
          person[field] !== "" &&
          person[field] !== null
        );
      });
    }

    // Check each group in the data array
    //////////////////////////////////////
    let adultsOrdinal = 0;
    return data?.every((group) => {
      // Check all adults in the group

      const adultsComplete = group.adults.every((adult) => {
        const isPrimaryAdult = adultsOrdinal === 0;
        adultsOrdinal += 1;
        const requiredAdultFields =
          requireContactForAllAdults || isPrimaryAdult
            ? [...adultFieldsBase, ...adultContactFields]
            : adultFieldsBase;
        return isComplete(adult, requiredAdultFields);
      });
      // Check all kids in the group
      const kidsComplete = group.kids.every((kid) =>
        isComplete(kid, kidFields)
      );

      return adultsComplete && kidsComplete;
    });
  }

  ////////////////////////////////////////////////
  function areAllRoomsSelected(dataToCheck = null) {
    const data = dataToCheck || roomsAndPaxInfo;
    if (!Array.isArray(data)) {
      return false;
    }
    const invalidIndex = data.findIndex(
      (group) => !group?.room?.[ROOM_KEYS.ROOM_TYPE]
    );
    if (invalidIndex !== -1) {
      return false;
    }
    return true;
  }

  //////////////////////////////////////
  const setRoomInfo = (newRoom, roomIndex) => {
    let tempPassengerInfo = JSON.parse(JSON.stringify(roomsAndPaxInfo));
    const previousRoom = tempPassengerInfo?.[roomIndex]?.room || null;
    const previousSlot = tempPassengerInfo?.[roomIndex] || null;
    const mergedRoom = {
      ...newRoom,
      addedFromRoomOptions:
        newRoom?.addedFromRoomOptions ??
        previousRoom?.addedFromRoomOptions ??
        previousSlot?.addedFromRoomOptions ??
        false,
      priceSource:
        newRoom?.priceSource ?? previousRoom?.priceSource ?? null,
      selectedAddRoomPriceIls:
        newRoom?.selectedAddRoomPriceIls ??
        previousRoom?.selectedAddRoomPriceIls ??
        previousSlot?.selectedAddRoomPriceIls ??
        null,
      selectedAddRoomBasePriceIls:
        newRoom?.selectedAddRoomBasePriceIls ??
        previousRoom?.selectedAddRoomBasePriceIls ??
        previousSlot?.selectedAddRoomBasePriceIls ??
        null,
      roomLevelHandleFeeIls:
        newRoom?.roomLevelHandleFeeIls ??
        previousRoom?.roomLevelHandleFeeIls ??
        previousSlot?.roomLevelHandleFeeIls ??
        null,
      displayedPriceWithMarkupIls:
        newRoom?.displayedPriceWithMarkupIls ??
        previousRoom?.displayedPriceWithMarkupIls ??
        previousSlot?.displayedPriceWithMarkupIls ??
        null,
      pricingSnapshot:
        newRoom?.pricingSnapshot ??
        previousRoom?.pricingSnapshot ??
        previousSlot?.pricingSnapshot ??
        null,
    };
    setRoomInfo_helper(
      mergedRoom,
      tempPassengerInfo,
      roomIndex,
      tempPassengerInfo.length >= 2
        ? buildMultiRoomComboOptions(selectedResult, extendedDealApiData)
        : null
    );
    setRoomsAndPaxInfo(tempPassengerInfo);
    const normalizedSelectedRoom =
      newRoom && typeof newRoom === "object"
        ? {
            ...newRoom,
            roomId:
              newRoom.roomId ??
              newRoom.RoomId ??
              newRoom.roomID ??
              newRoom[ROOM_KEYS.SEQ_NUM] ??
              newRoom["@seqNum"] ??
              null,
            RoomId:
              newRoom.RoomId ??
              newRoom.roomId ??
              newRoom.roomID ??
              newRoom[ROOM_KEYS.SEQ_NUM] ??
              newRoom["@seqNum"] ??
              null,
            "@seqNum":
              newRoom["@seqNum"] ??
              newRoom[ROOM_KEYS.SEQ_NUM] ??
              newRoom.roomId ??
              newRoom.RoomId ??
              null,
          }
        : null;
    if (normalizedSelectedRoom) {
      const publicBasePriceToAgentIls =
        resolvePublicBasePriceToAgentFromRoom(normalizedSelectedRoom);
      const publicSelectedRoomId = resolvePublicSelectedRoomId(normalizedSelectedRoom);
      const publicSelectedRoomSnapshot = {
        room_id: publicSelectedRoomId,
        room_type:
          normalizedSelectedRoom?.roomType ??
          normalizedSelectedRoom?.roomName ??
          null,
        base_price_to_agent_ils: publicBasePriceToAgentIls,
        our_total_fare:
          normalizedSelectedRoom?.ourFareDetails?.ourTotalFare ?? null,
        updated_at: Date.now(),
      };
      setSelectedRoom(normalizedSelectedRoom);
      _setSelectedResult((prev) => {
        if (!prev || typeof prev !== "object") return prev;
        const next = {
          ...prev,
          selectedRoom: normalizedSelectedRoom,
          public_base_price_to_agent_ils: publicBasePriceToAgentIls,
          public_selected_room_id: publicSelectedRoomId,
          public_selected_room_snapshot: publicSelectedRoomSnapshot,
        };
        if (prev.fullDeal && typeof prev.fullDeal === "object") {
          next.fullDeal = {
            ...prev.fullDeal,
            selectedRoom: normalizedSelectedRoom,
            public_base_price_to_agent_ils: publicBasePriceToAgentIls,
            public_selected_room_id: publicSelectedRoomId,
            public_selected_room_snapshot: publicSelectedRoomSnapshot,
          };
        }
        try {
          console.log("[STEP_3_STATE_AFTER_SET]", {
            "selectedResult.public_base_price_to_agent_ils":
              next?.public_base_price_to_agent_ils ?? null,
            "selectedResult.fullDeal.public_base_price_to_agent_ils":
              next?.fullDeal?.public_base_price_to_agent_ils ?? null,
          });
        } catch (_) {
          /* ignore */
        }
        return next;
      });
    }
  };

  useEffect(() => {
    const selectedRoom = selectedResult?.selectedRoom;
    if (!selectedRoom || typeof selectedRoom !== "object") return;
    const nextBase = resolvePublicBasePriceToAgentFromRoom(selectedRoom);
    if (!(nextBase != null && nextBase > 0)) return;
    const currentBase = toFiniteNumber(selectedResult?.public_base_price_to_agent_ils);
    const fullDealBase = toFiniteNumber(
      selectedResult?.fullDeal?.public_base_price_to_agent_ils
    );
    if (
      currentBase != null &&
      fullDealBase != null &&
      Math.abs(currentBase - nextBase) < 0.01 &&
      Math.abs(fullDealBase - nextBase) < 0.01
    ) {
      return;
    }
    _setSelectedResult((prev) => {
      if (!prev || typeof prev !== "object") return prev;
      return {
        ...prev,
        public_base_price_to_agent_ils: nextBase,
        fullDeal:
          prev.fullDeal && typeof prev.fullDeal === "object"
            ? {
                ...prev.fullDeal,
                public_base_price_to_agent_ils: nextBase,
              }
            : prev.fullDeal,
      };
    });
  }, [
    selectedResult?.selectedRoom?.roomId,
    selectedResult?.selectedRoom?.RoomId,
    selectedResult?.selectedRoom?.["@seqNum"],
    selectedResult?.selectedRoom?.ourFareDetails?.ourTotalFare?.initialPrice,
    selectedResult?.selectedRoom?.ourFareDetails?.ourTotalFare?.hrCommission,
    selectedResult?.public_base_price_to_agent_ils,
    selectedResult?.fullDeal?.public_base_price_to_agent_ils,
  ]);

  const calculateOrganizedTourTotalPayment = (currency) => {
    const dealOurPrice = selectedResult?.ourPrice;
    const otf =
      dealOurPrice && typeof dealOurPrice === "object"
        ? dealOurPrice.ourTotalFare
        : null;
    if (!dealOurPrice || !otf || typeof otf !== "object") {
      if (import.meta.env.DEV) {
        console.log(
          "[calculateOrganizedTourTotalPayment] skip: no ourPrice.ourTotalFare (e.g. direct order load / flat ourPrice)",
          { hasOurPrice: Boolean(dealOurPrice) }
        );
      }
      return;
    }

    const basePriceWithCommission = otf.priceWithCommission;
    if (
      basePriceWithCommission == null ||
      !Number.isFinite(Number(basePriceWithCommission))
    ) {
      if (import.meta.env.DEV) {
        console.log(
          "[calculateOrganizedTourTotalPayment] skip: missing priceWithCommission on ourTotalFare"
        );
      }
      return;
    }

    let ourNewPrice = {
      priceWithCreditCardFee:
        otf.priceWithCommissionAndCreditCardFee,
      creditCardFee: otf.creditCardFee,
      handleFee: otf.handlingFee,
      priceWithHandleFee: 0,
      priceWithBaggageFare: 0,
      priceWithCommission: basePriceWithCommission, // Keep original price
      agentCommission: otf.agentCommission,
    };
    
    // Calculate handleFee from base price (before adding it) — % on PWC only
    const handleFee = calculateHandleFee(basePriceWithCommission);
    ourNewPrice.handleFee = handleFee;
    if (handleFeeData?.type === "%") {
      stampHandlingFeePercentMetadata(ourNewPrice, {
        percentValue: handleFeeData.value,
        baseIls: basePriceWithCommission,
        absoluteIls: handleFee,
      });
    }

    // Calculate priceWithHandleFee (base price + handleFee)
    ourNewPrice.priceWithHandleFee = round2(basePriceWithCommission + handleFee);
    
    // Calculate creditCardFee from priceWithHandleFee (not from priceWithCommission)
    const orgClearing = runPaymentCreditClearingOnBase({
      baseForFeeIls: ourNewPrice.priceWithHandleFee,
      feePercent: getCreditCardFeePercent(),
      roundMoney: round2,
    });
    ourNewPrice.creditCardFee = orgClearing.creditCardFee ?? 0;
    setCreditCardFee(ourNewPrice.creditCardFee);
    ourNewPrice.paymentClearingAuditSteps = orgClearing.auditSteps;
    
    // Add Trip Guaranty insurance price if selected
    const tripGuarantyPrice = tripGuarantySelected && tripGuarantyQuote && tripGuarantyQuote.price 
      ? parseFloat(tripGuarantyQuote.price) || 0 
      : 0;
    ourNewPrice.tripGuarantyPrice = round2(tripGuarantyPrice);
    
    // Calculate final price with credit card fee and Trip Guaranty
    ourNewPrice.priceWithCreditCardFee = round2(
      ourNewPrice.priceWithHandleFee + ourNewPrice.creditCardFee + tripGuarantyPrice
    );
    
    ourNewPrice.handleFeeData = handleFeeData;
    ourNewPrice.agentCommission = otf.agentCommission;
    
    console.log('[calculateOrganizedTourTotalPayment] Calculated price:', {
      basePriceWithCommission,
      handleFee,
      priceWithHandleFee: ourNewPrice.priceWithHandleFee,
      creditCardFee: ourNewPrice.creditCardFee,
      tripGuarantyPrice: tripGuarantyPrice,
      priceWithCreditCardFee: ourNewPrice.priceWithCreditCardFee,
      handleFeeData
    });
    
    setOurPrice(ourNewPrice);
    return ourNewPrice;
  };

  ////////////////////////////////################################
  /** Aggregates room fares + supplements into context `ourPrice` (calls setOurPrice). Not a pure function by design. */
  const calculateLdsTotalPayment = (roomsOverride, traceOpts = {}) => {
    // this code sum the total of each key (hrCommission, agentCommission, total)
    // NOTE: lds don't include infant price in findExtendedDeal, but this is already calculated in
    // RoomSelector
    if (!selectedResult) return;
    const isOrganizedTourDeal =
      (selectedResult.dealType ?? selectedResult.dealtype) === "OrganizedTour";
    const slotsEarly = Array.isArray(roomsOverride) ? roomsOverride : roomsAndPaxInfo;
    if (isOrganizedTourDeal) {
      const hasRoomFares = (Array.isArray(slotsEarly) ? slotsEarly : []).some((r) =>
        getVpSlotOurTotalFare(r)
      );
      // Before extended-room matrix (or empty selection): keep deal-level org-tour total.
      if (!hasRoomFares) {
        return calculateOrganizedTourTotalPayment(
          selectedResult.fareDetails?.currency
        );
      }
      // Multi-room / selected occupancy: fall through and sum slot fares like packages.
      if (import.meta.env.DEV) {
        console.log("[ORG_TOUR_MULTI_ROOM_TOTAL]", {
          rooms: Array.isArray(slotsEarly) ? slotsEarly.length : 0,
          source: traceOpts?.source ?? "calculateLdsTotalPayment",
        });
      }
    }

    const slots = slotsEarly;
    const traceSource = traceOpts?.source ?? "calculateLdsTotalPayment";

    const ourPrice = {};
    let originalCurrency = null;
    let finalPriceGBP = null;
    let priceWithCommissionGBP = null;
    let creditCardFeeGBP = null;
    let hrCommissionGBP = null;
    let agentCommissionGBP = null;
    
    const hasManualAddedRooms = roomsAndPaxInfoHasManualAddedRooms(slots);
    const roomSum = accumulateVpBillableRoomsIntoOurPrice(
      ourPrice,
      slots,
      isNumber,
      {
        handleFeeData,
        traceSource,
        recomputeTriggered: traceOpts?.recomputeTriggered === true,
      }
    );
    const summedRoomPwccIls = roomSum.roomPwccSumIls ?? roomSum.roomTotalIls;

    for (let i = 0; i < slots.length; i++) {
      const ourTotalFare = getVpSlotOurTotalFare(slots[i]);
      if (!ourTotalFare) continue;
      if (ourTotalFare.originalCurrency && !originalCurrency) {
        originalCurrency = ourTotalFare.originalCurrency;
      }
      if (ourTotalFare.finalPriceGBP) {
        finalPriceGBP = (finalPriceGBP || 0) + ourTotalFare.finalPriceGBP;
      }
      if (ourTotalFare.priceWithCommissionGBP) {
        priceWithCommissionGBP =
          (priceWithCommissionGBP || 0) + ourTotalFare.priceWithCommissionGBP;
      }
      if (ourTotalFare.creditCardFeeGBP) {
        creditCardFeeGBP = (creditCardFeeGBP || 0) + ourTotalFare.creditCardFeeGBP;
      }
      if (ourTotalFare.hrCommissionGBP) {
        hrCommissionGBP = (hrCommissionGBP || 0) + ourTotalFare.hrCommissionGBP;
      }
      if (ourTotalFare.agentCommissionGBP) {
        agentCommissionGBP =
          (agentCommissionGBP || 0) + ourTotalFare.agentCommissionGBP;
      }
    }

    // Multi-room VP: compose replaces per-slot sum only when NO manual add-room slots.
    // Manual rooms must stay additive (base + each added room).
    if (
      isVacationPackagesFlightsHotelAgentDeal(selectedResult) &&
      slots.length > 1 &&
      !hasManualAddedRooms &&
      typeof calculateLDSDealFareForRoomsCompositionRef.current === "function"
    ) {
      const fd = selectedResult?.fullDeal || selectedResult;
      if (fd && typeof fd === "object") {
        const hasComposeStamp = Object.prototype.hasOwnProperty.call(
          fd,
          "__hrComposeDealRefPayload",
        );

        let struct;
        if (hasComposeStamp) {
          const raw = fd.__hrComposeDealRefPayload;
          const secondArg =
            raw === null || raw === undefined ? null : Array.isArray(raw) ? raw : [raw];
          struct = calculateLDSDealFareForRoomsCompositionRef.current(fd, secondArg);
        } else {
          const primaryExtFallback =
            Array.isArray(extendedDealApiData) && extendedDealApiData.length > 0
              ? extendedDealApiData
              : null;
          struct = primaryExtFallback
            ? calculateLDSDealFareForRoomsCompositionRef.current(fd, primaryExtFallback)
            : calculateLDSDealFareForRoomsCompositionRef.current(
                fd,
                selectedResult?.shortenedDeal ?? null,
              );
        }
        if (struct?.dynamicPackageNormalizedOnly || !struct?.ourTotalFare) {
          struct =
            selectedResult?.shortenedDeal &&
            calculateLDSDealFareForRoomsCompositionRef.current(
              fd,
              selectedResult.shortenedDeal,
            );
        }
        const cvTf =
          struct &&
          !struct.dynamicPackageNormalizedOnly &&
          struct.ourTotalFare &&
          typeof struct.ourTotalFare === "object"
            ? struct.ourTotalFare
            : null;
        const composedPwccFull = cvTf ? Number(cvTf.priceWithCommissionAndCreditCardFee) : NaN;
        if (Number.isFinite(composedPwccFull) && composedPwccFull > 0 && cvTf) {
          for (const k of Object.keys(cvTf)) {
            if (!Object.prototype.hasOwnProperty.call(cvTf, k)) continue;
            if (!isNumber(cvTf[k])) continue;
            ourPrice[k] = round2(Number(cvTf[k]));
          }
        }
      }
    }

    // 🔧 FIX: Ensure priceWithCommission is always defined (default to 0 if missing)
    if (ourPrice.priceWithCommission === undefined || !isFinite(ourPrice.priceWithCommission)) {
      const hasAnyRoomFare = slots.some((r) => getVpSlotOurTotalFare(r));
      // לפני extended matrix — צפוי; warn רק כשכבר יש fare לחדרים אבל הסכום לא נספר (באג נתונים)
      const payload = {
        priceWithCommission: ourPrice.priceWithCommission,
        roomsAndPaxInfo: slots.map((r, i) => ({
          index: i,
          hasRoom: !!r.room,
          hasOurFareDetails: !!r.room?.ourFareDetails,
          hasOurTotalFare: !!r.room?.ourFareDetails?.ourTotalFare,
          priceWithCommission: r.room?.ourFareDetails?.ourTotalFare?.priceWithCommission
        }))
      };
      if (hasAnyRoomFare) {
        console.warn('[calculateLdsTotalPayment] ⚠️ priceWithCommission is undefined or invalid, defaulting to 0', payload);
      } else if (process.env.NODE_ENV === 'development') {
        console.debug('[calculateLdsTotalPayment] priceWithCommission missing before room fares (expected until extended matrix)', payload);
      }
      ourPrice.priceWithCommission = 0;
    }

    // SSOT: כשסכום החדרים חסר/0 אבל יש fare שבור (רק handleFee וכו׳) — אל תשאיר בסיס 0
    // (אחרת priceWithHandleFee = רק דמי טיפול)
    if (!isFinite(ourPrice.priceWithCommission) || ourPrice.priceWithCommission <= 0) {
      const fallbackBase = resolveLdsPackageBasePriceWithCommissionFallback({
        aggregatedPriceWithCommission: ourPrice.priceWithCommission,
        ourSavedLDSOrder,
        selectedResult,
        roomsAndPaxInfo: slots,
        roomsPriceTable,
      });
      if (fallbackBase != null && Number.isFinite(fallbackBase) && fallbackBase > 0) {
        ourPrice.priceWithCommission = round2(fallbackBase);
      }
    }
    
    // Add non-numeric fields to the sum
    if (originalCurrency) {
      ourPrice.originalCurrency = originalCurrency;
    }
    if (finalPriceGBP) {
      ourPrice.finalPriceGBP = round2(finalPriceGBP);
    }
    if (priceWithCommissionGBP) {
      ourPrice.priceWithCommissionGBP = round2(priceWithCommissionGBP);
    }
    if (creditCardFeeGBP) {
      ourPrice.creditCardFeeGBP = round2(creditCardFeeGBP);
    }
    if (hrCommissionGBP) {
      ourPrice.hrCommissionGBP = round2(hrCommissionGBP);
    }
    if (agentCommissionGBP) {
      ourPrice.agentCommissionGBP = round2(agentCommissionGBP);
    }

    let baggageFare = 0;
    let supplementUncheckedCount = 0;
    if (ldsSupplement && Object.keys(ldsSupplement).length > 0) {
      for (const suppKey in ldsSupplement) {
        const supp = ldsSupplement[suppKey];
        // Include supplement in price if it's checked (agent selected it)
        // For saved orders, supplements should already be marked as checked: true
        if (supp.checked) {
          const fareToAdd = supp.ourFare || 0;
          baggageFare += fareToAdd;
        } else {
          supplementUncheckedCount += 1;
        }
      }
      if (supplementUncheckedCount > 0 && process.env.NODE_ENV === 'development') {
        console.debug(
          '[calculateLdsTotalPayment] supplements not in total (unchecked)',
          { count: supplementUncheckedCount }
        );
      }
    }
    ourPrice.baggagePrice = round2(baggageFare);

    // 🔧 FIX: Ensure priceWithCommission is a valid number before calculations
    const validPriceWithCommission = ourPrice.priceWithCommission || 0;
    ourPrice.priceWithBaggageFare = round2(
      validPriceWithCommission + baggageFare
    );

    // 🔧 FIX: Ensure priceWithBaggageFare is valid before calculating handleFee
    const validPriceWithBaggageFare = isFinite(ourPrice.priceWithBaggageFare) ? ourPrice.priceWithBaggageFare : validPriceWithCommission;
    const vpFlightsHotelFixedMarkup =
      selectedResult &&
      isVacationPackagesFlightsHotelAgentDeal(selectedResult) &&
      handleFeeData?.type === "+";

    const roomLevelHandleFeesIls = round2(roomSum.roomLevelHandleFeesIls ?? 0);
    const feeMeta = {
      handleFeeDataType: handleFeeData?.type ?? null,
      handleFeeDataValue: handleFeeData?.value ?? null,
      vacationPackageRoomsMarkupSig,
    };

    let handleFee;
    let creditCardFee;
    let finalTotalIls;

    if (vpFlightsHotelFixedMarkup) {
      const tripGuarantyPrice =
        tripGuarantySelected && tripGuarantyQuote && tripGuarantyQuote.price
          ? parseFloat(tripGuarantyQuote.price) || 0
          : 0;
      ourPrice.tripGuarantyPrice = round2(tripGuarantyPrice);
      // Multi-room search: while not every slot is selected, room-sum under-prices
      // (1 of 2 rooms → 1×). Prefer compose PWCC written above until selection is complete.
      const { basePwccIls, usedCompose } = resolveVpFixedMarkupBasePwccIls({
        summedRoomPwccIls,
        composedPwccIls: ourPrice.priceWithCommissionAndCreditCardFee,
        slotsLength: slots.length,
        includedCount: roomSum.includedCount ?? 0,
        hasManualAddedRooms,
      });
      // Compose / stale room PWCC omit agent extra commission — add it once to סה״כ לתשלום.
      const payable = resolveVpFixedMarkupPayableTotal({
        basePwccIls,
        usedCompose,
        summedRoomPwccIls,
        roomsBaseTotal: roomSum.roomsBaseTotal,
        roomLevelHandleFeesIls,
        configuredRoomMarkupIls: sumVacationPackageConfiguredRoomMarkupIls(
          slots,
          handleFeeData
        ),
        hasManualRoomMarkup: vacationPackageHasManualRoomMarkup(slots),
        handleFeeData,
        existingCreditCardFee: ourPrice.creditCardFee,
        baggageFare,
        tripGuarantyPrice,
        creditCardFeePercent: getCreditCardFeePercent(),
      });
      handleFee = payable.handleFee;
      creditCardFee = payable.creditCardFee;
      finalTotalIls = payable.finalTotalIls;
      ourPrice.handleFee = handleFee;
      ourPrice.creditCardFee = creditCardFee;
      ourPrice.priceWithHandleFee = round2(
        (ourPrice.priceWithCommission || 0) + handleFee
      );
      if (payable.extraMarkupIls > 0) {
        ourPrice.priceWithCommissionAndCreditCardFee = round2(
          (Number(ourPrice.priceWithCommissionAndCreditCardFee) || 0) +
            payable.extraMarkupIls +
            payable.extraCreditCardFeeIls
        );
      }
      ourPrice.priceWithCreditCardFee = finalTotalIls;
      ourPrice.paymentClearingAuditSteps = [payable.auditStep];
      setCreditCardFee(creditCardFee);
    } else {
      // % על priceWithCommission (כולל מרווח HR), לא על כבודה — כמו טיול מאורגן / ציפיית סוכן.
      // Never use priceWithBaggageFare as % base (product rule).
      if (handleFeeData?.type === "%") {
        handleFee = calculateHandleFee(validPriceWithCommission);
        ourPrice.handleFee = handleFee;
        stampHandlingFeePercentMetadata(ourPrice, {
          percentValue: handleFeeData.value,
          baseIls: validPriceWithCommission,
          absoluteIls: handleFee,
        });
        ourPrice.priceWithHandleFee = round2(
          validPriceWithCommission + handleFee
        );
      } else {
        handleFee = calculateHandleFee(validPriceWithBaggageFare);
        ourPrice.handleFee = handleFee;
        ourPrice.priceWithHandleFee = round2(
          validPriceWithBaggageFare + handleFee
        );
      }

      const validPriceWithHandleFee = isFinite(ourPrice.priceWithHandleFee)
        ? ourPrice.priceWithHandleFee
        : handleFeeData?.type === "%"
          ? validPriceWithCommission
          : validPriceWithBaggageFare;
      const clearingBaseIls =
        handleFeeData?.type === "%"
          ? round2(validPriceWithHandleFee + baggageFare)
          : validPriceWithHandleFee;
      const ldsClearing = runPaymentCreditClearingOnBase({
        baseForFeeIls: clearingBaseIls,
        feePercent: getCreditCardFeePercent(),
        roundMoney: round2,
      });
      creditCardFee = ldsClearing.creditCardFee ?? 0;
      ourPrice.creditCardFee = creditCardFee;
      setCreditCardFee(creditCardFee);
      ourPrice.paymentClearingAuditSteps = ldsClearing.auditSteps;

      const tripGuarantyPrice =
        tripGuarantySelected && tripGuarantyQuote && tripGuarantyQuote.price
          ? parseFloat(tripGuarantyQuote.price) || 0
          : 0;
      ourPrice.tripGuarantyPrice = round2(tripGuarantyPrice);
      finalTotalIls = round2(
        clearingBaseIls + creditCardFee + tripGuarantyPrice
      );
      ourPrice.priceWithCreditCardFee = finalTotalIls;
    }

    ourPrice.handleFeeData = handleFeeData;

    logVpHandleFeeRecomputeTrace({
      source: traceSource,
      handleFee: handleFeeData?.value ?? null,
      handleFeeType: handleFeeData?.type ?? null,
      roomIndex: null,
      roomLevelFee: null,
      roomsBaseTotal: roomSum.roomsBaseTotal,
      roomsWithFeeTotal: summedRoomPwccIls,
      baggageTotal: round2(baggageFare),
      creditCardFee,
      finalTotal: finalTotalIls,
      recomputeTriggered: traceOpts?.recomputeTriggered === true,
      reason: vpFlightsHotelFixedMarkup
        ? ourPrice.paymentClearingAuditSteps?.[0] ?? "vp_fixed_markup_sum_room_pwcc"
        : "order_level_handle_and_cc",
    });

    if (isOrganizedTourDeal) {
      const dealAfterHr = Number(
        selectedResult?.ourPrice?.ourTotalFare?.priceWithCommission
      );
      const summedPackage = Number(ourPrice.priceWithCommission);
      // Extended-room reprice was replacing the search/TRACE after-HR total
      // (₪10,359 → ₪10,024). Keep the deal price unless the room sum is higher.
      if (
        Number.isFinite(dealAfterHr) &&
        dealAfterHr > 0 &&
        Number.isFinite(summedPackage) &&
        summedPackage + 0.51 < dealAfterHr
      ) {
        return calculateOrganizedTourTotalPayment(
          selectedResult.fareDetails?.currency
        );
      }
    }

    logVpTotalPaymentRecomputeTrace({
      roomCount: slots.length,
      hasManualAddedRooms,
      roomTotalIls: summedRoomPwccIls,
      baggageTotalIls: round2(baggageFare),
      handleFeeIls: round2(ourPrice.handleFee ?? 0),
      roomLevelHandleFeesIls,
      creditCardFeeIls: round2(creditCardFee ?? 0),
      finalTotalIls,
      feeMeta,
      dependencyChangedKeys: traceOpts?.dependencyChangedKeys ?? null,
      source: hasManualAddedRooms
        ? "per_slot_room_sum_with_manual_rooms"
        : slots.length > 1 && isVacationPackagesFlightsHotelAgentDeal(selectedResult)
          ? "compose_or_per_slot_sum"
          : "per_slot_room_sum",
      traceSource,
    });

    setOurPrice(ourPrice);

    return ourPrice;
  };
  calculateLdsTotalPaymentRef.current = calculateLdsTotalPayment;
  ////////////////////////////////

  /**
   * Absolute handling fee in ILS.
   * When type is "%", `baseIls` MUST be priceWithCommission (incl. HR), never baggage.
   */
  const calculateHandleFee = (baseIls) => {
    if (!handleFeeData || !handleFeeData.value || handleFeeData.value === 0) {
      return 0;
    }
    let handlingFee = handleFeeData.value;
    if (handleFeeData.type === "%") {
      const pwc = Number(baseIls);
      if (!Number.isFinite(pwc) || pwc <= 0) return 0;
      handlingFee = (pwc * handleFeeData.value) / 100;
    }

    return round2(handlingFee);
  };

  /** Stamp explicit % metadata on ourPrice for admin TRACE / persist audit. */
  const stampHandlingFeePercentMetadata = (target, { percentValue, baseIls, absoluteIls }) => {
    if (!target || typeof target !== "object") return;
    const pct = Number(percentValue);
    const base = round2(Number(baseIls) || 0);
    const abs =
      absoluteIls != null && Number.isFinite(Number(absoluteIls))
        ? round2(Number(absoluteIls))
        : pct > 0 && base > 0
          ? round2((base * pct) / 100)
          : 0;
    target.handlingFeePercentBase = "priceWithCommission";
    target.handlingFeePercentExcludesBaggage = true;
    target.handlingFeePercentBaseIls = base;
    target.handlingFeePercentValue = Number.isFinite(pct) ? pct : 0;
    target.handling_fee = abs;
    target.handleFee = abs;
  };

  // 🔧 FIX: Memoize handleFeeData to prevent infinite loops - use primitive values
  const handleFeeDataType = handleFeeData?.type || '+';
  const handleFeeDataValue = handleFeeData?.value || 0;
  const ldsSupplementKeysCount = ldsSupplement ? Object.keys(ldsSupplement).length : 0;
  
  // 🔧 FIX: Use ref to prevent infinite loops
  const isCalculatingRef = useRef(false);
  const lastCalculationRef = useRef({ 
    handleFeeDataType: null, 
    handleFeeDataValue: null, 
    roomsLength: null, 
    supplementKeys: null, 
    dealId: null,
    vacationPackageRoomsMarkupSig: null,
  });
  
  useEffect(() => {
    // Prevent multiple simultaneous calculations
    if (isCalculatingRef.current) {
      return;
    }
    
    // Check if anything actually changed
    const currentState = {
      handleFeeDataType,
      handleFeeDataValue,
      roomsLength: roomsAndPaxInfo.length,
      supplementKeys: ldsSupplementKeysCount,
      dealId: selectedResult?.dealId,
      tripGuarantySelected: tripGuarantySelected,
      tripGuarantyQuotePrice: tripGuarantyQuote?.price || null,
      vacationPackageRoomsMarkupSig,
    };
    
    const hasChanged = 
      lastCalculationRef.current.handleFeeDataType !== currentState.handleFeeDataType ||
      lastCalculationRef.current.handleFeeDataValue !== currentState.handleFeeDataValue ||
      lastCalculationRef.current.roomsLength !== currentState.roomsLength ||
      lastCalculationRef.current.supplementKeys !== currentState.supplementKeys ||
      lastCalculationRef.current.dealId !== currentState.dealId ||
      lastCalculationRef.current.tripGuarantySelected !== currentState.tripGuarantySelected ||
      lastCalculationRef.current.tripGuarantyQuotePrice !== currentState.tripGuarantyQuotePrice ||
      lastCalculationRef.current.vacationPackageRoomsMarkupSig !==
        currentState.vacationPackageRoomsMarkupSig;
    
    if (!hasChanged && lastCalculationRef.current.dealId !== null) {
      return;
    }
    
    if (selectedResult) {
      // Organized tours: sum selected/added room fares when present; else deal-level total
      if ((selectedResult.dealType ?? selectedResult.dealtype) === "OrganizedTour") {
        const hasValidRooms = roomsAndPaxInfo.some(
          (room) => room.room?.ourFareDetails?.ourTotalFare
        );
        if (hasValidRooms || selectedResult.ourPrice?.ourTotalFare) {
          isCalculatingRef.current = true;
          lastCalculationRef.current = currentState;

          try {
            if (hasValidRooms) {
              calculateLdsTotalPayment(undefined, {
                source: "org_tour_handle_fee_or_rooms_effect",
              });
            } else {
              calculateOrganizedTourTotalPayment(
                selectedResult.fareDetails?.currency
              );
            }
          } finally {
            setTimeout(() => {
              isCalculatingRef.current = false;
            }, 0);
          }
        }
        return;
      }
      
      // For packages/hotels, check if rooms are available
      if (roomsAndPaxInfo.length > 0) {
        if (publicSavedOfferPriceLockRef.current) {
          if (process.env.NODE_ENV === "development") {
            console.log(
              "[SelectedResultContext] skip calculateLdsTotalPayment — public saved offer price lock"
            );
          }
          return;
        }
        // Check if rooms have ourFareDetails before calculating
        const hasValidRooms = roomsAndPaxInfo.some(room => room.room?.ourFareDetails?.ourTotalFare);
        if (!hasValidRooms) {
          return;
        }
        
        isCalculatingRef.current = true;
        lastCalculationRef.current = currentState;

        try {
          calculateLdsTotalPayment(undefined, {
            source: "lds_total_payment_effect",
            recomputeTriggered: true,
            dependencyChangedKeys: [
              handleFeeDataType !== lastCalculationRef.current.handleFeeDataType
                ? "handleFeeDataType"
                : null,
              handleFeeDataValue !== lastCalculationRef.current.handleFeeDataValue
                ? "handleFeeDataValue"
                : null,
              vacationPackageRoomsMarkupSig !==
              lastCalculationRef.current.vacationPackageRoomsMarkupSig
                ? "vacationPackageRoomsMarkupSig"
                : null,
              roomsAndPaxInfo.length !== lastCalculationRef.current.roomsLength
                ? "roomsLength"
                : null,
            ].filter(Boolean),
          });
        } finally {
          // Use setTimeout to ensure state updates complete before allowing next calculation
          setTimeout(() => {
            isCalculatingRef.current = false;
          }, 0);
        }
      }
    }
  }, [
    handleFeeDataType,
    handleFeeDataValue,
    roomsAndPaxInfo.length,
    ldsSupplementKeysCount,
    selectedResult?.dealId,
    tripGuarantySelected,
    tripGuarantyQuote,
    vacationPackageRoomsMarkupSig,
  ]);

  ////////////////////////////////

  // Use ref to always get the latest roomsAndPaxInfo value
  const roomsAndPaxInfoRef = useRef(roomsAndPaxInfo);
  
  // Update ref whenever roomsAndPaxInfo changes
  useEffect(() => {
    roomsAndPaxInfoRef.current = roomsAndPaxInfo;
  }, [roomsAndPaxInfo]);
  
  const initLdsSupplements = useCallback((selectedResult, supplements = undefined, roomsAndPaxInfoOverride = undefined) => {
    // CRITICAL: Verify dealId matches before initializing supplements
    const currentDealId = selectedResult?.dealId;
    
    // If dealId doesn't match, abort immediately
    // Only log if this is a real issue (not just initialization)
    if (currentDealId && currentDealIdRef.current && currentDealId !== currentDealIdRef.current) {
      // Only warn if we've already initialized before (to avoid spam during initial renders)
      if (lastDealIdRef.current) {
        console.warn('[initLdsSupplements] DealId mismatch - aborting init', {
          expectedDealId: currentDealIdRef.current,
          actualDealId: currentDealId
        });
      }
      return;
    }
    
    // Update dealId tracking
    if (currentDealId) {
      currentDealIdRef.current = currentDealId;
      lastDealIdRef.current = currentDealId;
    }
    
    // Use provided roomsAndPaxInfo if available, otherwise use from ref (always latest value)
    // This ensures we always use the latest value when called from useEffect
    const currentRoomsAndPaxInfo = roomsAndPaxInfoOverride || roomsAndPaxInfoRef.current;
    
    const vpBaggageIlsQuoted =
      selectedResult && isVacationPackagesFlightsHotelAgentDeal(selectedResult);
    const dealFareCurrencyForLds =
      selectedResult?.fareDetails?.currency ??
      selectedResult?.fullDeal?.fareDetails?.currency ??
      "";
    const calculateBaggagePriceWithCommissions = (
      fare,
      supplementRow = null,
      positionTraceExtra = {}
    ) => {
      const traceExtraBase = {
        source: "initLdsSupplements",
        dealId: selectedResult?.dealId ?? selectedResult?.ID ?? null,
        fareDetailsCurrencyResolved:
          vpBaggageIlsQuoted ?
            normalizeVpLdsDealFareCurrency(dealFareCurrencyForLds)
          : null,
        ...positionTraceExtra,
      };
      const computeArgs = {
        fareUsd: fare,
        currencyRateUsd: currencyRate.USD,
        baggageFeeFlatUsd: getBaggageFeeFlat(),
        supplement: supplementRow,
        baggageSupplierFareAuthoritativeIls: false,
        vpBaggageApplyLdsDealCurrencyFx: vpBaggageIlsQuoted,
        vpLdsDealFareCurrencyIso: dealFareCurrencyForLds,
        exchangeRates: currencyRate,
        traceExtra: traceExtraBase,
      };
      if (vpBaggageIlsQuoted) {
        const { totalIls, trace } =
          computeSupplementPriceIlsWithAudit(computeArgs);
        return { ourFare: totalIls, vpBaggagePriceTrace: trace };
      }
      return {
        ourFare: computeSupplementPriceIls(computeArgs),
        vpBaggagePriceTrace: undefined,
      };
    };
    let { linkedFlights, supplement } = selectedResult;
    if (supplements) {
      // If supplements provided directly, validate dealId first
      if (currentDealId && currentDealIdRef.current && currentDealId !== currentDealIdRef.current) {
        console.warn('[initLdsSupplements] DealId mismatch when setting provided supplements');
        return;
      }
      _setLdsSupplement(supplements);
      return;
    }
    if (!supplement) return;

    const dealTypeForBaggage =
      selectedResult?.dealType ?? selectedResult?.dealtype ?? "";
    if (dealTypeForBaggage === "OrganizedTour") {
      if (import.meta.env.DEV) {
        console.log("[ORG_TOUR_PAYMENT_BAGGAGE_LOGIC_SKIPPED]", {
          source: "initLdsSupplements_no_explicit_passed",
        });
      }
      return;
    }

    if (typeof supplement === "object" && !Array.isArray(supplement)) {
      supplement = [supplement];
    }

    supplement = supplement.filter((supp) => {
      return supp.type === "TROLLEY" || supp.type === "LUGGAGE" || supp.type === "BAGGAGE";
    });

    const parsedSupplement = supplement?.map((item) => {
      const params = normalizeLdsSupplementParams(item.params);
      const wEntry = params.find((param) => param.name === "weight");
      const uEntry = params.find((param) => param.name === "unit");
      return {
        "@seqNum": item["@seqNum"],
        id: item.id,
        displayedpackageid: item.displayedpackageid,
        type: item.type === "BAGGAGE" ? "LUGGAGE" : item.type,
        description: item.desc,
        category: item.category,
        fare: parseFloat(item.fare),
        weight: wEntry?.value ? parseFloat(wEntry.value) : null,
        unit: uEntry?.value || null,
        fltRef: item.fltRef,
        paxRef: item.paxRef,
      };
    });

    ////////////////////////////////
    const ldsSupplement = {};
    let suppCount = 0;

    const flights = normalizeLinkedFlightsFlightArray(linkedFlights);
    const flightsForSupplements =
      flights.length > 0
        ? flights
        : [{ leg: { depCity: "", arrCity: "" } }];

    parsedSupplement.forEach((supplement, suppIndex) => {
      currentRoomsAndPaxInfo.forEach((roomInfo, roomIndex) => {
        // Include adults and kids for luggage supplements (not infants)
        const paxList = [...roomInfo.adults, ...roomInfo.kids];
        paxList.forEach((pax, adultIndex) => {
          if (String(supplement.fltRef) === "-3") {
            flightsForSupplements.forEach((flightNode, flightIndex) => {
              const leg = firstLegFromFlightNode(flightNode) || {};
              suppCount += 1;
              const seqNum = suppCount;
              const priceRow = calculateBaggagePriceWithCommissions(
                supplement.fare,
                supplement,
                {
                  seqNum,
                  fltRef: supplement.fltRef,
                  flightIndex,
                  supplementCatalogId:
                    supplement.id ?? supplement.displayedpackageid ?? null,
                }
              );
              ldsSupplement[seqNum] = {
                id: supplement.id,
                displayedpackageid: supplement.displayedpackageid,
                "@seqNum": seqNum,
                orig: {
                  flight: flightNode.leg,
                  supp: supplement,
                },
                pax,
                roomIndex,
                paxIndex: adultIndex,
                type: supplement.type,
                checked: false,
                description: supplement.description,
                weightText:
                  supplement.weight != null && supplement.unit != null
                    ? `${supplement.weight}${supplement.unit}`
                    : supplement.weight != null
                      ? `${supplement.weight}`
                      : "",
                flight: {
                  fltRef: supplement.fltRef,
                  depCity: leg.depCity || "",
                  arrCity: leg.arrCity || "",
                  flightIndex: flightIndex,
                },
                ourFare: priceRow.ourFare,
                ...(priceRow.vpBaggagePriceTrace
                  ? { vpBaggagePriceTrace: priceRow.vpBaggagePriceTrace }
                  : {}),
              };
            });
          } else {
            // For specific flight reference, determine which flight based on fltRef
            // fltRef "-2" or "0" usually means first flight (departure) - flight[0]
            // fltRef "1" usually means second flight (return) - flight[1]
            // fltRef "2" would be third flight, etc.
            let flightIndex = 0;
            const fltRef = supplement.fltRef;

            if (fltRef === "-2" || fltRef === "0" || fltRef === "-1") {
              flightIndex = 0; // First flight (departure)
            } else if (fltRef === "1") {
              flightIndex = 1; // Second flight (return)
            } else if (fltRef === "2") {
              flightIndex = 2; // Third flight (if exists)
            } else {
              // Try to parse as number to get flight index
              const parsedRef = parseInt(fltRef);
              if (!isNaN(parsedRef) && parsedRef >= 0) {
                flightIndex = parsedRef;
              } else {
                // Default to first flight if can't parse
                flightIndex = 0;
              }
            }

            const flightNode =
              flights.length > 0
                ? flights[flightIndex] ?? flights[0]
                : { leg: { depCity: "", arrCity: "" } };
            const leg = firstLegFromFlightNode(flightNode) || {};

            suppCount += 1;
            const seqNum = suppCount;
            const priceRow = calculateBaggagePriceWithCommissions(
              supplement.fare,
              supplement,
              {
                seqNum,
                fltRef: supplement.fltRef,
                flightIndex,
                supplementCatalogId:
                  supplement.id ?? supplement.displayedpackageid ?? null,
              }
            );
            ldsSupplement[seqNum] = {
              "@seqNum": seqNum,
              orig: {
                flight: flightNode.leg,
                supp: supplement,
              },
              pax,
              roomIndex,
              paxIndex: adultIndex,
              type: supplement.type,
              checked: false,
              description: supplement.description,
              weightText:
                supplement.weight != null && supplement.unit != null
                  ? `${supplement.weight}${supplement.unit}`
                  : supplement.weight != null
                    ? `${supplement.weight}`
                    : "",
              flight: {
                fltRef: supplement.fltRef,
                depCity: leg.depCity || "",
                arrCity: leg.arrCity || "",
                flightIndex: flightIndex,
              },
              ourFare: priceRow.ourFare,
              ...(priceRow.vpBaggagePriceTrace
                ? { vpBaggagePriceTrace: priceRow.vpBaggagePriceTrace }
                : {}),
            };
          }
        });
      });
    });

    // Set supplements using direct setter (validation happens in wrapper)
    _setLdsSupplement(ldsSupplement);
  }, [
    currencyRate,
    getBaggageFeeFlat,
    roomsAndPaxInfo,
    selectedResult?.dealId,
    selectedResult?.ID,
    selectedResult?.fareDetails?.currency,
  ]);

  const initLdsSupplementsRef = useRef(initLdsSupplements);
  initLdsSupplementsRef.current = initLdsSupplements;

  // Re-initialize supplements when rooms change (after room selection)
  // 🔧 FIX: Don't re-initialize if supplements already exist from DB (for customer links)
  // But DO initialize if supplements exist in selectedResult but not in state/DB
  // 🔧 FIX: Use ref to track if we've already initialized to prevent infinite loops
  const supplementsInitializedRef = useRef(false);
  const lastDealIdRef = useRef(null);
  const lastRoomsLengthRef = useRef(0);
  const prevSelectedResultRef = useRef(null);
  
  // Track previous rooms length to detect room additions/removals
  const prevRoomsLengthRef = useRef(0);

  // Stable identity: avoids re-creating context value every Provider render and effect churn in B2C package flow.
  const setSelectedResult_resetAllFields = useCallback((nextSelected) => {
    const currentRooms = roomsCompositionRef.current;
    const occupancySig = buildPackageOccupancySignature({ rooms: currentRooms });
    let normalizedNextSelected = ensureSelectedFlightOptionSSOT(nextSelected, {
      sourceIfMissing: "default_first",
    });
    if (normalizedNextSelected) {
      normalizedNextSelected = mergeCanonicalRoomIntoSelectedResult(
        normalizedNextSelected,
        currentRooms,
      );
    }
    if (
      normalizedNextSelected?.selectedRoom &&
      !selectedRoomMatchesSearchOccupancy(
        normalizedNextSelected.selectedRoom,
        currentRooms,
        getRoomsSetByCount,
      )
    ) {
      const withoutRoom = {
        ...normalizedNextSelected,
        selectedRoom: null,
      };
      copyComposeDealRefStamp(normalizedNextSelected, withoutRoom);
      normalizedNextSelected = withoutRoom;
    }
    const newDealId =
      normalizedNextSelected?.dealId ??
      normalizedNextSelected?.fullDeal?.dealId ??
      null;
    const nextRoomsAndPax = buildInitialRoomsAndPaxInfo({ rooms: currentRooms });

    // STEP 1: Clear ALL supplements IMMEDIATELY - no conditions, just clear
    console.log(
      "[SelectedResultContext] setSelectedResult_resetAllFields - Clearing all supplements for new package",
      { newDealId: newDealId }
    );
    _setLdsSupplement({}); // Use direct setter to bypass validation
    setOurPrice({});
    setCanonicalBackendProfilePricing(null);
    setOurSavedLDSOrder(null);
    supplementsInitializedRef.current = false;
    lastDealIdRef.current = null;
    prevSelectedResultRef.current = null;
    currentDealIdRef.current = newDealId || null; // Update dealId tracking

    // STEP 2: Reset all other fields from CURRENT search occupancy (ref, not stale closure)
    setRoomsAndPaxInfo(nextRoomsAndPax);
    setRoomsSelected(false);
    setFieldsFilled(false);
    setRoomsPriceTable([]);
    setExtendedDealApiData([]);
    setPackageProfilePriceBlockMessage(null);
    setHotelDetails(null);
    setHotelResource(null);
    setLdsHotelInfoNormalized(null);
    const preservedCanonicalRoom = canonicalRoomToSelectedRoom(
      normalizedNextSelected?.searchCardRoom
    );
    if (
      preservedCanonicalRoom &&
      selectedRoomMatchesSearchOccupancy(
        preservedCanonicalRoom,
        currentRooms,
        getRoomsSetByCount,
      )
    ) {
      setSelectedRoom(preservedCanonicalRoom);
    } else {
      setSelectedRoom(null);
    }
    setSelectedPublicRoomPriceSnapshot(null);

    logPackageOccupancyTrace("setSelectedResult_resetAllFields", {
      searchId: currentSearchRequestIdRef.current,
      selectedResultId: newDealId,
      ldsDealId:
        normalizedNextSelected?.ldsDealId ??
        normalizedNextSelected?.linkedFlights?.[0]?.ldsDealId ??
        null,
      roomsComposition: currentRooms,
      roomsAndPaxInfo: nextRoomsAndPax,
      selectedRoom: normalizedNextSelected?.selectedRoom ?? null,
      currentOccupancySignature: occupancySig,
      selectedRoomOccupancySignature: buildSelectedRoomOccupancySignature(
        normalizedNextSelected?.selectedRoom,
      ),
    });

    // STEP 3: Set selectedResult
    _setSelectedResult(normalizedNextSelected);
    logVacationFlightSelectionTrace(
      "navigation_payload_built",
      normalizedNextSelected,
      normalizedNextSelected?.selectedFlightOptionFromUser,
      { context: "SelectedResultContext.setSelectedResult_resetAllFields" }
    );

    // STEP 4: Initialize supplements for the new package (only if we have one)
    if (normalizedNextSelected) {
      // Use setTimeout to ensure state is cleared first
      setTimeout(() => {
        // Verify dealId still matches before initializing
        if (
          normalizedNextSelected?.dealId === newDealId &&
          currentDealIdRef.current === newDealId
        ) {
          initLdsSupplementsRef.current?.(normalizedNextSelected);
        } else {
          console.log(
            "[SelectedResultContext] DealId mismatch during init, skipping"
          );
        }
      }, 0);
    }

    setCurrentStageIndex(0);
    setHandleFeeData({ type: "+", value: 0 });
    setTripGuarantySelected(false);
    setTripGuarantyQuote(null);
  }, [setRoomsAndPaxInfo]);

  useEffect(() => {
    const onNewPackageSearch = () => {
      const pathname =
        typeof window !== "undefined" ? window.location.pathname || "" : "";
      if (isPersistedVacationPackageProfilePath(pathname)) {
        return;
      }
      const isPublicVacationPackages =
        pathname.includes("/vacation-packages") ||
        pathname.includes("/vacation_packages");
      if (isPublicVacationPackages) {
        const decision = resolvePassengerSlotsForSearchOccupancy({
          roomsComposition: roomsCompositionRef.current,
          roomsAndPaxInfo: roomsAndPaxInfoRef.current,
          ourSavedLDSOrder: null,
          selectedResultDealType:
            selectedResultRef.current?.dealType ||
            selectedResultRef.current?.dealtype ||
            "",
        });
        if (decision.action === "rebuild" && Array.isArray(decision.roomsAndPaxInfo)) {
          setRoomsAndPaxInfo(decision.roomsAndPaxInfo);
          setSelectedRoom(null);
          setSelectedPublicRoomPriceSnapshot(null);
          setRoomsPriceTable([]);
        }
        return;
      }
      setSelectedResult_resetAllFields(null);
    };
    window.addEventListener(
      HR_VACATION_PACKAGE_NEW_SEARCH_EVENT,
      onNewPackageSearch,
    );
    return () => {
      window.removeEventListener(
        HR_VACATION_PACKAGE_NEW_SEARCH_EVENT,
        onNewPackageSearch,
      );
    };
  }, [setRoomsAndPaxInfo, setSelectedResult_resetAllFields]);
  
  // ============================================
  // CLEAN REWRITE: Single useEffect to handle dealId changes
  // ============================================
  useEffect(() => {
    const currentDealId =
      selectedResult?.dealId ??
      selectedResult?.fullDeal?.dealId ??
      null;
    const prevDealId =
      prevSelectedResultRef.current?.dealId ??
      prevSelectedResultRef.current?.fullDeal?.dealId ??
      null;
    
    // Case 1: selectedResult is null - clear everything
    if (!selectedResult) {
      if (prevSelectedResultRef.current !== null) {
        console.log('[SelectedResultContext] selectedResult is null - clearing supplements');
        _setLdsSupplement({});
        setOurSavedLDSOrder(null);
        supplementsInitializedRef.current = false;
        currentDealIdRef.current = null;
        lastDealIdRef.current = null;
      }
      prevSelectedResultRef.current = null;
      return;
    }

    // Case 2: dealId changed - clear supplements IMMEDIATELY
    if (prevDealId && currentDealId && prevDealId !== currentDealId) {
      console.log('[SelectedResultContext] DealId changed - clearing supplements', {
        oldDealId: prevDealId,
        newDealId: currentDealId
      });
      
      // Clear supplements using direct setter
      _setLdsSupplement({});
      setOurSavedLDSOrder(null);
      supplementsInitializedRef.current = false;
      currentDealIdRef.current = currentDealId;
      lastDealIdRef.current = currentDealId;
    } else if (currentDealId) {
      // Case 3: Same dealId or new dealId - update tracking
      currentDealIdRef.current = currentDealId;
      lastDealIdRef.current = currentDealId;
    }
    
    prevSelectedResultRef.current = selectedResult;
  }, [selectedResult]);
  
  // ============================================
  // CLEAN REWRITE: Simplified supplement initialization
  // ============================================
  useEffect(() => {
    // Skip if no selectedResult
    if (!selectedResult) {
      return;
    }
    
    const currentDealId = selectedResult?.dealId;
    
    // CRITICAL: Verify dealId matches before doing anything
    if (currentDealId && currentDealIdRef.current && currentDealId !== currentDealIdRef.current) {
      console.warn('[SelectedResultContext] DealId mismatch in init effect - aborting', {
        expected: currentDealIdRef.current,
        actual: currentDealId
      });
      return;
    }
    
    // Check if rooms count changed (room added or removed)
    const roomsCountChanged = prevRoomsLengthRef.current !== roomsAndPaxInfo.length;
    const currentRoomsLength = roomsAndPaxInfo.length;
    
    // If rooms count changed, we MUST re-initialize supplements to include/exclude passengers from new/removed rooms
    if (roomsCountChanged) {
      // Reset initialization flag to allow re-initialization
      supplementsInitializedRef.current = false;
      prevRoomsLengthRef.current = currentRoomsLength;

      const orderSuppObj = parseSavedOrderSupplementToObject(ourSavedLDSOrder?.supplement);
      const hasSupplementsInDB = orderSuppObj != null;
      const savedOrderDealId = ourSavedLDSOrder?.dealId || ourSavedLDSOrder?.dealid;

      if (
        ourSavedLDSOrder?.id &&
        hasSupplementsInDB &&
        dealIdsLooselyEqual(savedOrderDealId, currentDealId)
      ) {
        const hasSupplementsInState =
          ldsSupplement && Object.keys(ldsSupplement).length > 0;
        if (hasSupplementsInState) {
          console.log(
            "[SelectedResultContext] roomsCountChanged but saved order supplements already in state — skip deal template init"
          );
          supplementsInitializedRef.current = true;
          lastDealIdRef.current = currentDealId;
          return;
        }
        try {
          const withChecked = supplementsWithCheckedFromOrderObject(orderSuppObj);
          initLdsSupplements(selectedResult, withChecked, roomsAndPaxInfo);
          supplementsInitializedRef.current = true;
          lastDealIdRef.current = currentDealId;
        } catch (error) {
          console.error(
            "[SelectedResultContext] Error hydrating supplements from saved order:",
            error
          );
          supplementsInitializedRef.current = false;
        }
        return;
      }
    } else {
      // Only skip if rooms count hasn't changed AND supplements are already initialized
      if (supplementsInitializedRef.current && currentDealId === lastDealIdRef.current) {
        return;
      }
      
      const orderSuppObjElse = parseSavedOrderSupplementToObject(ourSavedLDSOrder?.supplement);
      const hasSupplementsInDB = orderSuppObjElse != null;

      const savedOrderDealId = ourSavedLDSOrder?.dealId || ourSavedLDSOrder?.dealid;

      if (hasSupplementsInDB && dealIdsLooselyEqual(savedOrderDealId, currentDealId)) {
        console.log('[SelectedResultContext] Supplements exist in DB for same package - skipping init');
        supplementsInitializedRef.current = true;
        prevRoomsLengthRef.current = currentRoomsLength;
        return;
      }
      
      // If we have supplements in state for this package AND rooms count hasn't changed, skip
      const hasSupplementsInState = ldsSupplement && Object.keys(ldsSupplement).length > 0;
      if (hasSupplementsInState && currentDealId === lastDealIdRef.current) {
        console.log('[SelectedResultContext] Supplements already exist in state for same package - skipping init');
        supplementsInitializedRef.current = true;
        prevRoomsLengthRef.current = currentRoomsLength;
        return;
      }
    }
    
    // Initialize supplements from selectedResult if they exist
    if (selectedResult.supplement && roomsAndPaxInfo.length > 0) {
      const hasRoomsWithPassengers = roomsAndPaxInfo.some(
        (room) => (room.adults && room.adults.length > 0) || (room.kids && room.kids.length > 0)
      );
      
      if (hasRoomsWithPassengers) {
        console.log('[SelectedResultContext] Initializing supplements from selectedResult', {
          roomsCount: currentRoomsLength,
          roomsCountChanged,
          actualRoomsCount: roomsAndPaxInfo.length
        });
        
        // Pass roomsAndPaxInfo explicitly to ensure we use the current value from useEffect
        // This is critical when rooms count changed, as the ref might not be updated yet
        try {
          // Pass roomsAndPaxInfo explicitly to ensure we use the current value from useEffect
          initLdsSupplements(selectedResult, undefined, roomsAndPaxInfo);
          supplementsInitializedRef.current = true;
          lastDealIdRef.current = currentDealId;
          prevRoomsLengthRef.current = currentRoomsLength;
        } catch (error) {
          console.error('[SelectedResultContext] Error initializing supplements:', error);
          supplementsInitializedRef.current = false;
        }
      }
    } else {
      // Update prevRoomsLengthRef even if no supplements to initialize
      prevRoomsLengthRef.current = currentRoomsLength;
    }
  }, [roomsAndPaxInfo, roomsAndPaxInfo.length, selectedResult?.dealId, selectedResult?.supplement, initLdsSupplements, ourSavedLDSOrder?.supplement, ourSavedLDSOrder?.id, ourSavedLDSOrder?.dealId, ourSavedLDSOrder?.dealid, ldsSupplement]);
  ////////////////////////////////

  const getPaxFullNameBySupplement = (supp) => {
    let fullName = "";
    roomsAndPaxInfo.forEach((roomInfo) => {
      const paxList = [...roomInfo.adults, ...roomInfo.kids];
      paxList.forEach((pax, adultIndex) => {
        if (pax["@seqNum"] === supp.pax["@seqNum"]) {
          fullName = `${pax.firstname} ${pax.lastname}`;
          supp.pax.firstname = pax.firstname;
          supp.pax.lastname = pax.lastname;
        }
      });
    });
    return fullName;
  };

  const updateNameInSupplement = (roomsAndPaxInfoArg) => {
    if (!Array.isArray(roomsAndPaxInfoArg) || roomsAndPaxInfoArg.length === 0) {
      return;
    }
    if (!ldsSupplement || Object.keys(ldsSupplement).length === 0) {
      return;
    }
    // Create a shallow copy of ldsSupplement to avoid direct mutation
    const ldsSupplementCopy = { ...ldsSupplement };
    let changed = false;
    for (let roomIndex = 0; roomIndex < roomsAndPaxInfoArg.length; roomIndex++) {
      const roomInfo = roomsAndPaxInfoArg[roomIndex];
      const paxList = [...roomInfo.adults, ...roomInfo.kids];
      for (let paxIndex = 0; paxIndex < paxList.length; paxIndex++) {
        const pax = paxList[paxIndex];
        for (const suppKey in ldsSupplementCopy) {
          const supp = ldsSupplementCopy[suppKey];
          if (!supp?.pax || pax["@seqNum"] !== supp.pax["@seqNum"]) continue;
          const nextPax = {
            ...supp.pax,
            title: pax.title,
            firstname: pax.firstname,
            lastname: pax.lastname,
            email: pax.email,
            cell: pax.cell,
            "@seqNum": pax["@seqNum"],
          };
          const same =
            nextPax.title === supp.pax.title &&
            nextPax.firstname === supp.pax.firstname &&
            nextPax.lastname === supp.pax.lastname &&
            nextPax.email === supp.pax.email &&
            nextPax.cell === supp.pax.cell;
          if (!same) {
            supp.pax = nextPax;
            changed = true;
          }
        }
      }
    }
    if (changed) {
      setLdsSupplement(ldsSupplementCopy);
    }
  };
  ////////////////////////////////

  const getSupplementsAsType = () => {
    const suppRes = {};
    
    // 🔧 FIX: In organized tours, filter out luggage supplements (LUGGAGE and TROLLEY)
    // because baggage is already included in the tour price
    const isOrganizedTour = selectedResult?.dealType === "OrganizedTour";
    
    // First, get supplements from state
    if (ldsSupplement && Object.keys(ldsSupplement).length > 0) {
      console.log('[getSupplementsAsType] Processing supplements from state', {
        totalSupplements: Object.keys(ldsSupplement).length,
        roomsCount: roomsAndPaxInfoRef.current?.length || 0
      });
      
      for (const suppKey in ldsSupplement) {
        const supp = ldsSupplement[suppKey];
        // Skip luggage supplements in organized tours
        if (isOrganizedTour && (supp.type === "LUGGAGE" || supp.type === "TROLLEY" || supp.type === "BAGGAGE")) {
          continue;
        }
        if (suppRes[supp.type]) {
          suppRes[supp.type].push(supp);
        } else {
          suppRes[supp.type] = [supp];
        }
      }
    }
    
    // If no supplements in state, try to initialize from selectedResult
    // This ensures supplements are available even if not yet initialized
    if (Object.keys(suppRes).length === 0 && selectedResult?.supplement) {
      // Try to initialize if we have rooms with passengers
      if (roomsAndPaxInfo.length > 0) {
        const hasRoomsWithPassengers = roomsAndPaxInfo.some(
          (room) => (room.adults && room.adults.length > 0) || (room.kids && room.kids.length > 0)
        );
        if (hasRoomsWithPassengers) {
          console.log('[getSupplementsAsType] No supplements in state, initializing from selectedResult');
          try {
            // Initialize supplements immediately (synchronously if possible)
            initLdsSupplements(selectedResult);
            // After initialization, try to get supplements from state again
            // Note: This might not work immediately due to async state update,
            // but it will work on next render
          } catch (error) {
            console.warn('[getSupplementsAsType] Failed to initialize supplements:', error);
          }
        } else {
          console.log('[getSupplementsAsType] Supplements exist but no passengers yet - will initialize when passengers are added');
        }
      } else {
        console.log('[getSupplementsAsType] Supplements exist but no rooms yet - will initialize when rooms are selected');
      }
      
      // 🔧 FIX: Even if we can't initialize yet, try to return supplements from selectedResult
      // This ensures supplements section is shown even before initialization
      if (Object.keys(suppRes).length === 0) {
        console.log('[getSupplementsAsType] Supplements exist in selectedResult but not yet initialized - attempting to show them anyway');
        // Try to get supplements from selectedResult and convert them to the expected format
        let supplements = selectedResult.supplement;
        if (typeof supplements === "object" && !Array.isArray(supplements)) {
          supplements = [supplements];
        }
        
        // Filter only luggage and trolley supplements
        // 🔧 FIX: In organized tours, don't show luggage supplements (they're included in price)
        const luggageSupplements = supplements.filter((supp) => {
          const suppType = supp.type || supp.Type || '';
          // Skip luggage supplements in organized tours
          if (isOrganizedTour) {
            return false;
          }
          return suppType === "TROLLEY" || suppType === "LUGGAGE" || suppType === "BAGGAGE";
        });
        
        if (luggageSupplements.length > 0) {
          console.log('[getSupplementsAsType] Found', luggageSupplements.length, 'luggage supplements in selectedResult');
          
          // Try to find passenger names from roomsAndPaxInfo if available
          const getPaxInfo = (paxRef) => {
            if (!roomsAndPaxInfo || roomsAndPaxInfo.length === 0) {
              return { "@seqNum": paxRef || 1, firstname: '', lastname: '' };
            }
            
            // Try to find passenger by seqNum
            for (const roomInfo of roomsAndPaxInfo) {
              // Include adults and kids (not infants)
              const paxList = [...(roomInfo.adults || []), ...(roomInfo.kids || [])];
              const pax = paxList.find(p => p["@seqNum"] === paxRef || p.seqNum === paxRef);
              if (pax) {
                return {
                  "@seqNum": pax["@seqNum"] || pax.seqNum || paxRef || 1,
                  firstname: pax.firstname || pax.firstName || '',
                  lastname: pax.lastname || pax.lastName || '',
                };
              }
            }
            
            // If not found, return default
            return { "@seqNum": paxRef || 1, firstname: '', lastname: '' };
          };
          
          // Group by type
            luggageSupplements.forEach((supp, index) => {
            // Normalize BAGGAGE to LUGGAGE for consistency
            const normalizedType = supp.type === "BAGGAGE" ? "LUGGAGE" : supp.type;
            if (!suppRes[normalizedType]) {
              suppRes[normalizedType] = [];
            }
            
            // Get passenger info if available
            const paxInfo = getPaxInfo(supp.paxRef);
            const pList = normalizeLdsSupplementParams(supp.params);
            const wVal = pList.find((param) => param.name === "weight")?.value;
            const uVal = pList.find((param) => param.name === "unit")?.value || "";
            const weightText =
              wVal != null && wVal !== ""
                ? `${wVal}${uVal || ""}`
                : "";
            const lfFlights = normalizeLinkedFlightsFlightArray(
              selectedResult?.linkedFlights
            );
            const firstNode = lfFlights[0] || { leg: { depCity: "", arrCity: "" } };
            const firstLeg = firstLegFromFlightNode(firstNode) || {};
            const rawFare = parseFloat(supp.fare);
            
            // Create a basic supplement object that can be displayed
            // This is a temporary format until proper initialization
            // Use a unique seqNum that combines type, paxRef, and index to ensure uniqueness
            const uniqueSeqNum = supp["@seqNum"] || `temp-${supp.type}-${supp.paxRef || 'all'}-${index}-${Date.now()}`;
            suppRes[normalizedType].push({
              "@seqNum": uniqueSeqNum,
              id: supp.id,
              type: normalizedType, // Use normalized type
              description: supp.desc || supp.description || '',
              checked: false,
              weightText,
              flight: {
                fltRef: supp.fltRef || "-2",
                depCity: firstLeg.depCity || "",
                arrCity: firstLeg.arrCity || "",
              },
              pax: paxInfo,
              ourFare: 0, // Will be calculated on initialization
              orig: {
                supp: {
                  fare: Number.isFinite(rawFare) ? rawFare : supp.fare,
                  category: supp.category,
                  type: normalizedType,
                  description: supp.desc || supp.description,
                },
              },
            });
          });
        }
      }
    }
    
    return suppRes;
  };
  ////////////////////////////////
  const hasLdsSupplements = useMemo(() => {
    // For organized tours, check if baggage is already included in the price
    if (selectedResult?.dealType === "OrganizedTour") {
      // Check if supplements exist
      const hasSupplementsFromState = ldsSupplement && Object.keys(ldsSupplement).length > 0;
      const hasSupplementsFromOrder = ourSavedLDSOrder?.supplement && 
        typeof ourSavedLDSOrder.supplement === 'object' && 
        Object.keys(ourSavedLDSOrder.supplement).length > 0;
      const hasSupplementsFromSelectedResult = selectedResult?.supplement && 
        (Array.isArray(selectedResult.supplement) ? selectedResult.supplement.length > 0 : 
         typeof selectedResult.supplement === 'object' && Object.keys(selectedResult.supplement).length > 0);
      
      // If supplements exist, check if they are optional (not included in base price)
      // In organized tours, if baggage is included in the deal, supplements might be optional extras
      // We should only show supplements section if there are optional supplements to add
      if (hasSupplementsFromState || hasSupplementsFromOrder || hasSupplementsFromSelectedResult) {
        // Check if supplements have a price (if price is 0, they might be included)
        let hasOptionalSupplements = false;
        let totalSupplements = 0;
        
        // Check supplements from state
        if (hasSupplementsFromState && ldsSupplement) {
          for (const suppKey in ldsSupplement) {
            const supp = ldsSupplement[suppKey];
            totalSupplements++;
            // If supplement has a price > 0, it's optional
            if (supp.ourFare && parseFloat(supp.ourFare) > 0) {
              hasOptionalSupplements = true;
              break;
            }
          }
        }
        
        // Check supplements from order
        if (!hasOptionalSupplements && hasSupplementsFromOrder) {
          let orderSupplements = ourSavedLDSOrder.supplement;
          if (typeof orderSupplements === 'string') {
            try {
              orderSupplements = JSON.parse(orderSupplements);
            } catch (e) {
              orderSupplements = {};
            }
          }
          if (typeof orderSupplements === 'object' && !Array.isArray(orderSupplements)) {
            orderSupplements = Object.values(orderSupplements);
          }
          
          // Check if any supplement has a price > 0 (optional)
          for (const supp of orderSupplements) {
            totalSupplements++;
            const fare = supp.ourFare || supp.fare || supp.price || 0;
            if (parseFloat(fare) > 0) {
              hasOptionalSupplements = true;
              break;
            }
          }
        }
        
        // Check supplements from selectedResult
        if (!hasOptionalSupplements && hasSupplementsFromSelectedResult) {
          let supplements = selectedResult.supplement;
          if (typeof supplements === "object" && !Array.isArray(supplements)) {
            supplements = [supplements];
          }
          
          // Check if any supplement has a fare > 0 (optional)
          for (const supp of supplements) {
            totalSupplements++;
            const fare = supp.fare || supp.price || 0;
            if (parseFloat(fare) > 0) {
              hasOptionalSupplements = true;
              break;
            }
          }
        }
        
        // 🔧 FIX: In organized tours, always hide luggage supplements (LUGGAGE and TROLLEY)
        // because baggage is already included in the tour price
        // Only show supplements if there are optional supplements to add (non-luggage)
        // If all supplements are included (price = 0) or are luggage, don't show the section
        
        // Check if supplements are only luggage/trolley
        let hasOnlyLuggageSupplements = true;
        if (hasSupplementsFromState && ldsSupplement) {
          for (const suppKey in ldsSupplement) {
            const supp = ldsSupplement[suppKey];
            if (supp.type !== "LUGGAGE" && supp.type !== "TROLLEY" && supp.type !== "BAGGAGE") {
              hasOnlyLuggageSupplements = false;
              break;
            }
          }
        }
        
        if (!hasOnlyLuggageSupplements && hasSupplementsFromOrder) {
          let orderSupplements = ourSavedLDSOrder.supplement;
          if (typeof orderSupplements === 'string') {
            try {
              orderSupplements = JSON.parse(orderSupplements);
            } catch (e) {
              orderSupplements = {};
            }
          }
          if (typeof orderSupplements === 'object' && !Array.isArray(orderSupplements)) {
            orderSupplements = Object.values(orderSupplements);
          }
          
          for (const supp of orderSupplements) {
            const suppType = supp.type || supp.Type || '';
            if (suppType !== "LUGGAGE" && suppType !== "TROLLEY" && suppType !== "BAGGAGE") {
              hasOnlyLuggageSupplements = false;
              break;
            }
          }
        }
        
        if (!hasOnlyLuggageSupplements && hasSupplementsFromSelectedResult) {
          let supplements = selectedResult.supplement;
          if (typeof supplements === "object" && !Array.isArray(supplements)) {
            supplements = [supplements];
          }
          
          for (const supp of supplements) {
            const suppType = supp.type || supp.Type || '';
            if (suppType !== "LUGGAGE" && suppType !== "TROLLEY" && suppType !== "BAGGAGE") {
              hasOnlyLuggageSupplements = false;
              break;
            }
          }
        }
        
        // Hide supplements section if only luggage supplements exist (they're included in tour price)
        if (hasOnlyLuggageSupplements && totalSupplements > 0) {
          console.log('[hasLdsSupplements] Organized tour has', totalSupplements, 'luggage supplements - hiding supplements section (luggage is included in tour price)');
          return false;
        }
        
        // Also hide if all supplements are included (price = 0)
        if (totalSupplements > 0 && !hasOptionalSupplements) {
          console.log('[hasLdsSupplements] Organized tour has', totalSupplements, 'supplements but they are all included in price (price = 0) - not showing supplements section');
          return false;
        }
        
        // If we found optional supplements, show the section
        if (hasOptionalSupplements) {
          return true;
        }
      }
      
      // If no supplements exist at all, don't show the section
      return false;
    }
    
    // For other deal types, use original logic
    // Check supplements from state
    const hasSupplementsFromState = ldsSupplement && Object.keys(ldsSupplement).length > 0;
    // Also check supplements from saved order (for customer links or when state not loaded yet)
    const hasSupplementsFromOrder = ourSavedLDSOrder?.supplement && 
      typeof ourSavedLDSOrder.supplement === 'object' && 
      Object.keys(ourSavedLDSOrder.supplement).length > 0;
    // 🔧 FIX: Also check supplements from selectedResult (for when supplements exist in deal but not in DB)
    const hasSupplementsFromSelectedResult = selectedResult?.supplement && 
      (Array.isArray(selectedResult.supplement) ? selectedResult.supplement.length > 0 : 
       typeof selectedResult.supplement === 'object' && Object.keys(selectedResult.supplement).length > 0);
    // Show supplements if they exist in state, saved order, or selectedResult
    const hasSupplements = hasSupplementsFromState || hasSupplementsFromOrder || hasSupplementsFromSelectedResult;
    // Only log when value changes to avoid spam
    if (hasSupplements) {
      console.log('[hasLdsSupplements] Supplements detected:', {
        hasLdsSupplement: !!ldsSupplement,
        keysLength: ldsSupplement ? Object.keys(ldsSupplement).length : 0,
        hasSupplementsFromState,
        hasSupplementsFromOrder,
        hasSupplementsFromSelectedResult,
      });
    }
    return hasSupplements;
  }, [ldsSupplement, ourSavedLDSOrder?.supplement, selectedResult?.supplement, selectedResult?.dealType]);

  const ilsToUsd = useCallback(
    (ils, fixed = true) => {
      const rate = currencyRate?.USD;
      if (!rate || !isFinite(rate) || !isFinite(ils)) return 0;
      const value = ils / rate;
      return fixed ? Math.ceil(value * 100) / 100 : value;
    },
    [currencyRate?.USD]
  );

  const usdToIls = useCallback(
    (usd, fixed = true) => {
      const rate = currencyRate?.USD;
      if (!rate || !isFinite(rate) || !isFinite(usd)) return 0;
      const value = usd * rate;
      return fixed ? Math.ceil(value * 100) / 100 : value;
    },
    [currencyRate?.USD]
  );

  // Periodically check if fields are filled (to catch autofill)
  // This ensures that when browser autofill fills the fields, the validation is updated
  React.useEffect(() => {
    if (shouldUsePerfStub) {
      return;
    }
    if (!roomsAndPaxInfo || roomsAndPaxInfo.length === 0) {
      setFieldsFilled(false);
      setRoomsSelected(false);
      return;
    }
    
    // Check immediately when roomsAndPaxInfo changes
    const allFilled = areAllFieldsFilled();
    const allSelected = areAllRoomsSelected();
    setFieldsFilled((prev) => (Object.is(prev, allFilled) ? prev : allFilled));
    setRoomsSelected((prev) => (Object.is(prev, allSelected) ? prev : allSelected));
    
    // Only check periodically for autofill if fields are not filled yet.
    // Keep this as a short burst (not indefinite polling) to avoid background
    // churn during unrelated dashboard interactions (e.g. side-nav clicks).
    if (!allFilled) {
      autofillIntervalTickRef.current = 0;
      const intervalId = setInterval(() => {
        const currentAllFilled = areAllFieldsFilled();
        const currentAllSelected = areAllRoomsSelected();
        autofillIntervalTickRef.current += 1;
        const maxAutofillTicks = 6;
        setFieldsFilled((prev) =>
          Object.is(prev, currentAllFilled) ? prev : currentAllFilled
        );
        setRoomsSelected((prev) =>
          Object.is(prev, currentAllSelected) ? prev : currentAllSelected
        );
        
        // Clear interval once all fields are filled
        if (currentAllFilled && currentAllSelected) {
          clearInterval(intervalId);
          return;
        }

        if (autofillIntervalTickRef.current >= maxAutofillTicks) {
          clearInterval(intervalId);
        }
      }, 500);
      
      return () => {
        clearInterval(intervalId);
      };
    }
  }, [roomsAndPaxInfo]);

  const ldsPriceCompareDebugRef = useRef(null);
  ldsPriceCompareDebugRef.current = {
    selectedResult,
    extendedDealApiData,
    roomsAndPaxInfo,
    ourPrice,
    currencyRate,
    agentData,
    categories,
  };

  useEffect(() => {
    if (process.env.NODE_ENV !== "development") {
      return undefined;
    }
    window.__ldsDebugPayload = () =>
      buildLdsPriceComparePayload(ldsPriceCompareDebugRef.current);
    window.__ldsDebugSnapshot = () => ({ ...ldsPriceCompareDebugRef.current });
    let cancelled = false;
    import("../utils/debug/compareTopLdsResults.js").then((m) => {
      if (cancelled) return;
      window.__compareTopLdsResults = m.compareTopLdsResults;
      window.__buildLdsPriceCompareSnapshotFromDeal =
        m.buildLdsPriceCompareSnapshotFromDeal;
    });
    return () => {
      cancelled = true;
      try {
        delete window.__ldsDebugPayload;
        delete window.__ldsDebugSnapshot;
        delete window.__compareTopLdsResults;
        delete window.__buildLdsPriceCompareSnapshotFromDeal;
      } catch (_) {
        /* ignore */
      }
    };
  }, []);

  const contextValue = useMemo(
    () => ({
      selectedResult,
      currentStageIndex,
      setCurrentStageIndex,
      searchTab,
      roomsAndPaxInfo,
      fieldsFilled,
      roomsSelected,
      ourSavedLDSOrder,
      // FX (same object as AppContext — required for search cards / packageobject USD gate)
      currencyRate,
      // funcs
      ilsToUsd,
      usdToIls,
      moveNextStage,
      setRoomsAndPaxInfo,
      setSelectedResult,
      setOurSavedLDSOrder,
      setRoomInfo,
      setExtendedDealApiData,
      extendedDealApiData,
      hotelDetails,
      setHotelDetails,
      hotelResource,
      setHotelResource,
      ldsHotelInfoNormalized,
      setLdsHotelInfoNormalized,
      isConditionsConfirmed,
      setIsConditionsConfirmed,
      selectedRoom,
      setSelectedRoom,
      selectedPublicRoomPriceSnapshot,
      setSelectedPublicRoomPriceSnapshot,
      handleFeeData,
      updateHandleFeeData,
      ldsSupplement,
      setLdsSupplement,
      calculateLdsTotalPayment,
      creditCardFee,
      setSelectedResult_resetAllFields,
      getPaxFullNameBySupplement,
      getSupplementsAsType,
      ourPrice,
      canonicalBackendProfilePricing,
      setCanonicalBackendProfilePricing,
      packageProfileDisplayOurPrice,
      setOurPrice,
      calculateOrganizedTourTotalPayment,
      roomsPriceTable,
      hasLdsSupplements,
      setHandleFeeData,
      agentData,
      setAgentData,
      addHandlingComission,
      initLdsSupplements,
      setDynamicOrder,
      dynamicOrder,
      setHotelOrder,
      hotelOrder,
      linkedFlightsDealPriceHandleFee,
      linkedFlightsDealPrice,
      tripGuarantySelected,
      setTripGuarantySelected,
      tripGuarantyQuote,
      setTripGuarantyQuote,
      setPublicSavedOfferPriceLock,
      isPublicSavedOfferPriceLocked,
      packageProfilePriceBlockMessage,
      setPackageProfilePriceBlockMessage,
      ldsPassengerFieldErrors,
      setLdsPassengerFieldErrors,
      clearLdsPassengerFieldErrors,
    }),
    [
      selectedResult,
      currentStageIndex,
      searchTab,
      roomsAndPaxInfo,
      fieldsFilled,
      roomsSelected,
      ourSavedLDSOrder,
      currencyRate,
      ilsToUsd,
      usdToIls,
      moveNextStage,
      setRoomsAndPaxInfo,
      setSelectedResult,
      setOurSavedLDSOrder,
      setRoomInfo,
      setExtendedDealApiData,
      extendedDealApiData,
      hotelDetails,
      setHotelDetails,
      hotelResource,
      setHotelResource,
      ldsHotelInfoNormalized,
      setLdsHotelInfoNormalized,
      isConditionsConfirmed,
      setIsConditionsConfirmed,
      selectedRoom,
      setSelectedRoom,
      selectedPublicRoomPriceSnapshot,
      setSelectedPublicRoomPriceSnapshot,
      handleFeeData,
      updateHandleFeeData,
      ldsSupplement,
      setLdsSupplement,
      calculateLdsTotalPayment,
      creditCardFee,
      setSelectedResult_resetAllFields,
      getPaxFullNameBySupplement,
      getSupplementsAsType,
      ourPrice,
      canonicalBackendProfilePricing,
      setCanonicalBackendProfilePricing,
      packageProfileDisplayOurPrice,
      setOurPrice,
      calculateOrganizedTourTotalPayment,
      roomsPriceTable,
      hasLdsSupplements,
      setHandleFeeData,
      agentData,
      setAgentData,
      addHandlingComission,
      initLdsSupplements,
      setDynamicOrder,
      dynamicOrder,
      setHotelOrder,
      hotelOrder,
      linkedFlightsDealPriceHandleFee,
      linkedFlightsDealPrice,
      tripGuarantySelected,
      tripGuarantyQuote,
      setPublicSavedOfferPriceLock,
      isPublicSavedOfferPriceLocked,
      packageProfilePriceBlockMessage,
      setPackageProfilePriceBlockMessage,
      ldsPassengerFieldErrors,
      setLdsPassengerFieldErrors,
      clearLdsPassengerFieldErrors,
    ]
  );

  const perfStubValue = useMemo(
    () => ({
      selectedResult: null,
      currentStageIndex: 0,
      setCurrentStageIndex: () => {},
      searchTab: null,
      roomsAndPaxInfo: [],
      fieldsFilled: false,
      roomsSelected: false,
      ourSavedLDSOrder: null,
      currencyRate: { USD: 1, EUR: 1, GBP: 1 },
      ilsToUsd: () => 0,
      usdToIls: () => 0,
      moveNextStage: () => {},
      setRoomsAndPaxInfo: () => {},
      setSelectedResult: () => {},
      setOurSavedLDSOrder: () => {},
      setRoomInfo: () => {},
      setExtendedDealApiData: () => {},
      extendedDealApiData: [],
      hotelDetails: null,
      setHotelDetails: () => {},
      hotelResource: null,
      setHotelResource: () => {},
      ldsHotelInfoNormalized: null,
      setLdsHotelInfoNormalized: () => {},
      isConditionsConfirmed: false,
      setIsConditionsConfirmed: () => {},
      selectedRoom: null,
      setSelectedRoom: () => {},
      selectedPublicRoomPriceSnapshot: null,
      setSelectedPublicRoomPriceSnapshot: () => {},
      handleFeeData: { type: "+", value: 0 },
      updateHandleFeeData: () => {},
      ldsSupplement: {},
      setLdsSupplement: () => {},
      calculateLdsTotalPayment: () => 0,
      creditCardFee: 0,
      setSelectedResult_resetAllFields: () => {},
      getPaxFullNameBySupplement: () => "",
      getSupplementsAsType: () => [],
      ourPrice: {},
      canonicalBackendProfilePricing: null,
      setCanonicalBackendProfilePricing: () => {},
      packageProfileDisplayOurPrice: {},
      setOurPrice: () => {},
      calculateOrganizedTourTotalPayment: () => 0,
      roomsPriceTable: [],
      hasLdsSupplements: false,
      setHandleFeeData: () => {},
      agentData: null,
      setAgentData: () => {},
      addHandlingComission: () => 0,
      initLdsSupplements: () => {},
      setDynamicOrder: () => {},
      dynamicOrder: null,
      setHotelOrder: () => {},
      hotelOrder: null,
      linkedFlightsDealPriceHandleFee: 0,
      linkedFlightsDealPrice: 0,
      tripGuarantySelected: false,
      setTripGuarantySelected: () => {},
      tripGuarantyQuote: null,
      setTripGuarantyQuote: () => {},
      setPublicSavedOfferPriceLock: () => {},
      isPublicSavedOfferPriceLocked: false,
      packageProfilePriceBlockMessage: null,
      setPackageProfilePriceBlockMessage: () => {},
    }),
    []
  );

  const providerValue = shouldUsePerfStub ? perfStubValue : contextValue;

  return (
    <SelectedResultContext.Provider value={providerValue}>
      {children}
    </SelectedResultContext.Provider>
  );
};

//////////////////////////////////////
export const useSelectedResult = () => {
  const context = useContext(SelectedResultContext);
  if (context === undefined) {
    console.warn(
      "useSelectedResult must be used within a SelectedResultProvider"
    );
    return null;
  }
  return context;
};

export default SelectedResultContext;

function buildMultiRoomComboOptions(selectedResult, extendedDealApiData) {
  const sr = ensureSelectedFlightOptionSSOT(selectedResult);
  const flightOpt =
    sr?.selectedFlightOptionFromUser ||
    buildSelectedFlightOptionFromUser({
      selectedResult: sr,
      selectedFlight: sr?.linkedFlights,
      selectedFlightIndex: sr?.selectedFlightIndex,
      source: "default_first",
      linkedFlightsPool: sr?.linkedFlights,
    });
  const { rows, sourcePath } = resolveExtendedDealRowsForLdsMultiRoom({
    extendedDealApiData,
    selectedResult: sr,
  });
  return {
    linkedFlights: flightOpt?.linkedFlights ?? sr?.linkedFlights ?? null,
    selectedFlightOptionFromUser: flightOpt,
    selectedResult: sr,
    extendedDealApiData: rows,
    extendedDealSourcePath: sourcePath,
    _resolvedExtendedRows: rows,
    context: "SelectedResultContext.roomPick",
  };
}

function setRoomInfo_helper(newRoom, tempPassengerInfo, roomIndex, comboOptions = null) {
  const {
    adultFare,
    totalFare,
    roomSet: roomSetFromPick,
    roomType,
    kidFare,
    infantFare,
    ourFareDetails,
    ldsDealId,
    public_base_price_to_agent_ils,
    "@seqNum": seqNum,
  } = newRoom;
  const resolvedPublicBase =
    toFiniteNumber(public_base_price_to_agent_ils) ??
    resolvePublicBasePriceToAgentFromRoom(newRoom);
  const prevRoom = tempPassengerInfo[roomIndex]?.room;
  const prevSlot = tempPassengerInfo[roomIndex];
  let roomSet = roomSetFromPick ?? prevRoom?.[ROOM_KEYS.ROOM_SET] ?? prevRoom?.roomSet;
  let resolvedLdsDealId =
    (ldsDealId != null && String(ldsDealId).trim()) ||
    resolveBookableLdsDealIdFromExtendedRoom(newRoom) ||
    "";
  if (comboOptions && tempPassengerInfo.length >= 2) {
    const previewSlot = {
      ...prevSlot,
      room: {
        ...(prevRoom || {}),
        roomSet,
        roomType: roomType ?? prevRoom?.[ROOM_KEYS.ROOM_TYPE] ?? prevRoom?.roomType,
        "@seqNum": seqNum ?? prevRoom?.[ROOM_KEYS.SEQ_NUM] ?? prevRoom?.["@seqNum"],
        ldsDealId,
      },
    };
    const combo = resolvePerRoomComboLdsDealId(previewSlot, roomIndex, comboOptions);
    if (combo) resolvedLdsDealId = combo;
    if (!roomSet) {
      const extRows = normalizeLdsExtendedDealRows(comboOptions.extendedDealApiData);
      const extRow = extRows.find((row) => {
        const rowSeq = row["@seqNum"] ?? row.seqNum ?? row.roomId;
        const pickSeq = seqNum ?? prevRoom?.[ROOM_KEYS.SEQ_NUM];
        return pickSeq != null && rowSeq != null && String(rowSeq) === String(pickSeq);
      });
      const extSet = extRow?.roomSet ?? extRow?.RoomSet;
      if (extSet) roomSet = extSet;
    }
  }
  tempPassengerInfo[roomIndex].room = {
    [ROOM_KEYS.ROOM_SET]: roomSet,
    [ROOM_KEYS.ROOM_TYPE]: roomType,
    [ROOM_KEYS.SEQ_NUM]: seqNum,
    [ROOM_KEYS.OUR_FARE_DETAILS]: ourFareDetails,
    [ROOM_KEYS.DEAL_FARE]: { adultFare, totalFare, kidFare, infantFare },
    [ROOM_KEYS.LDS_DEAL_ID]: resolvedLdsDealId,
    public_base_price_to_agent_ils: resolvedPublicBase,
    addedFromRoomOptions:
      newRoom.addedFromRoomOptions === true ||
      prevRoom?.addedFromRoomOptions === true ||
      prevSlot?.addedFromRoomOptions === true,
    priceSource:
      newRoom.priceSource ?? prevRoom?.priceSource ?? null,
    selectedAddRoomPriceIls:
      newRoom.selectedAddRoomPriceIls ??
      prevRoom?.selectedAddRoomPriceIls ??
      prevSlot?.selectedAddRoomPriceIls ??
      null,
    selectedAddRoomBasePriceIls:
      newRoom.selectedAddRoomBasePriceIls ??
      prevRoom?.selectedAddRoomBasePriceIls ??
      prevSlot?.selectedAddRoomBasePriceIls ??
      null,
    selectedAddRoomDealSeq:
      newRoom.selectedAddRoomDealSeq ??
      prevRoom?.selectedAddRoomDealSeq ??
      prevSlot?.selectedAddRoomDealSeq ??
      null,
    selectedAddRoomRoomType:
      newRoom.selectedAddRoomRoomType ??
      prevRoom?.selectedAddRoomRoomType ??
      prevSlot?.selectedAddRoomRoomType ??
      null,
    selectedAddRoomOccupancySignature:
      newRoom.selectedAddRoomOccupancySignature ??
      prevRoom?.selectedAddRoomOccupancySignature ??
      prevSlot?.selectedAddRoomOccupancySignature ??
      null,
    roomLevelHandleFeeIls:
      newRoom.roomLevelHandleFeeIls ??
      prevRoom?.roomLevelHandleFeeIls ??
      prevSlot?.roomLevelHandleFeeIls ??
      null,
    displayedPriceWithMarkupIls:
      newRoom.displayedPriceWithMarkupIls ??
      prevRoom?.displayedPriceWithMarkupIls ??
      prevSlot?.displayedPriceWithMarkupIls ??
      null,
    pricingSnapshot:
      newRoom.pricingSnapshot ??
      prevRoom?.pricingSnapshot ??
      prevSlot?.pricingSnapshot ??
      null,
  };
  const nextSlot = tempPassengerInfo[roomIndex];
  if (newRoom.addedFromRoomOptions === true) {
    nextSlot.addedFromRoomOptions = true;
    if (newRoom.roomLevelHandleFeeIls != null) {
      nextSlot.roomLevelHandleFeeIls = newRoom.roomLevelHandleFeeIls;
    }
    if (newRoom.displayedPriceWithMarkupIls != null) {
      nextSlot.displayedPriceWithMarkupIls = newRoom.displayedPriceWithMarkupIls;
    }
  }
  if (
    isVpManualAddedRoom(nextSlot.room, nextSlot) &&
    ourFareDetails?.ourTotalFare
  ) {
    const freeze = buildVpManualAddRoomFreezeFields({
      occupancy: {
        adults: Array.isArray(nextSlot.adults) ? nextSlot.adults.length : 0,
        kids: Array.isArray(nextSlot.kids) ? nextSlot.kids.length : 0,
        infants: Array.isArray(nextSlot.infants) ? nextSlot.infants.length : 0,
        roomSet,
        roomType,
        dealSeq: seqNum,
      },
      roomSuggestion: {
        ...newRoom,
        roomSet,
        roomType,
        "@seqNum": seqNum,
      },
      ourFareDetails,
      pricingSnapshot: newRoom.pricingSnapshot,
    });
    applyVpManualAddRoomFreezeToRoomAndSlot(nextSlot, freeze);
  } else if (nextSlot.room?.pricingSnapshot) {
    nextSlot.pricingSnapshot = nextSlot.room.pricingSnapshot;
  }
}
